/**
 * EDI Workflow Hub (EDI-WH) — Client Modular Engine Entrypoint
 */
import { API_BASE, sanitize, showToast } from './modules/config.js';
import { Theme } from './modules/theme.js';
import { Auth } from './modules/auth.js';
import { apiRequest } from './modules/api.js';

const EDISIG = {
  sanitize(str) {
    return sanitize(str);
  },
  showToast(icon, title) {
    return showToast(icon, title);
  },
  apiRequest(endpoint, options) {
    return apiRequest(endpoint, options);
  },
  activeJobId: null,
  pollingInterval: null,
  currentRoute: '#/dashboard',
  bondsTable: null,
  auditTable: null,
  expandedBondIds: new Set(),
  currentUser: null,
  
  // Initialize SPA Router and global hooks
  init() {
    Theme.init();

    // Add routing listeners
    window.addEventListener('hashchange', () => this.handleRouting());

    // Setup login form submission handler
    $(document).on('submit', '#login-form', (e) => Auth.handleLogin(e, () => {
      if (!window.location.hash || window.location.hash === '#' || window.location.hash === '#/login') {
        window.location.hash = '#/dashboard';
      } else {
        this.handleRouting();
      }
    }));
    $(document).on('click', '#btn-logout', (e) => {
      e.preventDefault();
      Auth.logout();
    });

    Auth.checkAuth().then(() => {
      if (Auth.isAuthenticated()) {
        this.currentUser = Auth.currentUser;
        this.handleRouting();
        this.checkActiveScrapeJob();
      }
    });
  },

  checkAuth() {
    return Auth.checkAuth();
  },

  isAuthenticated() {
    return Auth.isAuthenticated();
  },

  setCurrentUser(user) {
    return Auth.setCurrentUser(user);
  },

  showLoginPage() {
    return Auth.showLoginPage();
  },

  logout() {
    return Auth.logout();
  },

  // Initialize theme from localStorage or system preference
  initTheme() {
    const savedTheme = localStorage.getItem('edi-sig-theme');
    
    // Default to light mode as requested by user
    const initialTheme = savedTheme || 'light';
    this.setTheme(initialTheme);

    // Event listener for theme toggle click
    $(document).on('click', '#theme-toggle-btn', (e) => {
      e.preventDefault();
      const currentTheme = localStorage.getItem('edi-sig-theme') || 'light';
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      this.setTheme(newTheme);
    });
  },

  // Apply layout files and style classes based on theme choice
  setTheme(theme) {
    localStorage.setItem('edi-sig-theme', theme);
    const link = document.getElementById('layout-stylesheet');
    const toggleIcon = document.getElementById('theme-toggle-icon');
    
    if (theme === 'light') {
      if (link) link.setAttribute('href', 'assets/css/demo1/style.css');
      document.body.classList.remove('sidebar-dark');
      document.body.classList.add('sidebar-light', 'light-theme');
      
      if (toggleIcon) {
        toggleIcon.setAttribute('data-feather', 'moon');
        toggleIcon.setAttribute('class', 'text-muted');
      }
    } else {
      if (link) link.setAttribute('href', 'assets/css/demo2/style.css');
      document.body.classList.remove('sidebar-light', 'light-theme');
      document.body.classList.add('sidebar-dark');
      
      if (toggleIcon) {
        toggleIcon.setAttribute('data-feather', 'sun');
        toggleIcon.setAttribute('class', 'text-warning');
      }
    }
    
    if (typeof feather !== 'undefined') {
      feather.replace();
    }
  },
  
  // Handle login submit
  async handleLogin(e) {
    e.preventDefault();
    const username = $('#login-username').val().trim();
    const password = $('#login-password').val();
    const errorDiv = $('#login-error');
    const submitBtn = $('#login-submit-btn');
    
    errorDiv.hide().text('');
    submitBtn.prop('disabled', true);
    submitBtn.find('.login-btn-text').hide();
    submitBtn.find('.login-btn-spinner').show();

    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Invalid login credentials.');
      }

      const data = await response.json();
      localStorage.setItem('edi_sig_token', data.token);
      this.setCurrentUser(data.user);
      
      this.showToast('success', 'Logged in successfully.');
      if (!window.location.hash || window.location.hash === '#' || window.location.hash === '#/login') {
        window.location.hash = '#/dashboard';
      } else {
        this.handleRouting();
      }
      this.checkActiveScrapeJob();
    } catch (err) {
      errorDiv.text(err.message).show();
    } finally {
      submitBtn.prop('disabled', false);
      submitBtn.find('.login-btn-text').show();
      submitBtn.find('.login-btn-spinner').hide();
    }
  },

  logout() {
    localStorage.removeItem('edi_sig_token');
    this.currentUser = null;
    this.showLoginPage();
    window.location.hash = '#/dashboard';
  },

  // Client-side SPA Routing System
  async handleRouting() {
    if (!this.isAuthenticated()) {
      this.showLoginPage();
      return;
    }

    const rawRoute = window.location.hash || '#/dashboard';
    const parts = rawRoute.split('?');
    const route = parts[0];
    this.currentRoute = route;
    
    // Check admin-only routes
    if (route.startsWith('#/admin') && (!this.currentUser || this.currentUser.role_name !== 'admin')) {
      this.showToast('error', 'Access denied. Administrator privileges required.');
      window.location.hash = '#/dashboard';
      return;
    }

    // Parse query params
    this.queryParams = new URLSearchParams(parts[1] || '');
    
    // Update sidebar navigation active states
    $('.sidebar .nav-item').removeClass('active');
    $(`.sidebar .nav-item a[href="${route}"]`).parent().addClass('active');

    // Show loader
    $('#page-loader').css('opacity', '1').show();
    
    // Reset any page-specific polling or variables on route change
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    try {
      const mainContent = $('#main-content-target');
      switch (route) {
        case '#/dashboard':
          await this.renderDashboard(mainContent);
          break;
        case '#/ingest':
          await this.renderIngestion(mainContent);
          break;
        case '#/batch-qa':
          await this.renderBatchQA(mainContent);
          break;
        case '#/job-verify':
          await this.renderJobVerificationPage(mainContent);
          break;
        case '#/jobs':
          await this.renderIngestion(mainContent);
          break;
        case '#/data-explorer':
          await this.renderDataExplorer(mainContent);
          break;
        case '#/audit-log':
          await this.renderAuditLog(mainContent);
          break;
        case '#/schedules':
          await this.renderSchedules(mainContent);
          break;
        case '#/settings':
          await this.renderSettings(mainContent);
          break;
        case '#/admin/users':
          await this.renderUserManagement(mainContent);
          break;
        case '#/admin/roles':
          await this.renderRoleManagement(mainContent);
          break;
        default:
          window.location.hash = '#/dashboard';
      }
      
      // Initialize Feather icons after render
      if (typeof feather !== 'undefined') {
        feather.replace();
      }
    } catch (err) {
      console.error('Page render failed:', err);
      this.showToast('error', 'Failed to load page content.');
    } finally {
      // Hide loader
      setTimeout(() => {
        $('#page-loader').animate({ opacity: 0 }, 200, function() {
          $(this).hide();
        });
      }, 100);
    }
  },

  /**
   * Utility helper to generate styled HTML badge for exchange source.
   * Resolves by MIC code, exchange module, or ISIN country prefix.
   * Falls back to a neutral "Unknown Exchange" badge when unresolvable.
   */
  getExchangeBadge(micCode, isin, exchangeModule, extraClasses = '') {
    const mic = (micCode || '').trim().toUpperCase();
    const isinCode = (isin || '').trim().toUpperCase();
    const mod = (exchangeModule || '').trim().toLowerCase();
    const classes = extraClasses ? ` ${extraClasses}` : '';

    if (mic === 'XSWX' || mic === 'ZUR' || mod === 'six_swiss' || mod === 'six' || (isinCode && (isinCode.startsWith('CH') || isinCode.startsWith('LI')))) {
      return `<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50${classes}">🇨🇭 SIX (XSWX)</span>`;
    }
    if (mic === 'XSTU' || mic === 'STU' || mod === 'boerse_stuttgart' || mod === 'stuttgart') {
      return `<span class="badge bg-primary bg-opacity-25 text-primary border border-primary border-opacity-50${classes}">🇩🇪 Stuttgart (XSTU)</span>`;
    }
    if (mic === 'XFRA' || mic === 'FRA' || mod === 'frankfurt' || mod === 'boerse_frankfurt') {
      return `<span class="badge bg-primary bg-opacity-25 text-primary border border-primary border-opacity-50${classes}">🇩🇪 Frankfurt (XFRA)</span>`;
    }
    if (mic === 'XPAR' || mic === 'PAR' || mod === 'euronext_paris' || mod === 'paris') {
      return `<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50${classes}">🇫🇷 Euronext Paris (XPAR)</span>`;
    }
    if (mic === 'XPTY' || mic === 'PTY' || mod === 'latinex' || mod === 'panama' || (isinCode && isinCode.startsWith('PA'))) {
      return `<span class="badge bg-warning bg-opacity-25 text-warning border border-warning border-opacity-50${classes}">🇵🇦 Latinex (XPTY)</span>`;
    }
    if (mic === 'BVPASA' || mic === 'XASU' || mic === 'ASU' || mod === 'bvpasa' || mod === 'asuncion' || (isinCode && isinCode.startsWith('PY'))) {
      return `<span class="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50${classes}">🇵🇾 BVPASA (XASU)</span>`;
    }
    if (mic === 'XNYS' || mod === 'nyse') {
      return `<span class="badge bg-primary bg-opacity-25 text-primary border border-primary border-opacity-50${classes}">🇺🇸 NYSE (XNYS)</span>`;
    }
    if (mic === 'XNAS' || mod === 'nasdaq') {
      return `<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50${classes}">🇺🇸 NASDAQ (XNAS)</span>`;
    }
    if (mic === 'XLON' || mod === 'lse') {
      return `<span class="badge bg-secondary bg-opacity-25 text-white border border-secondary border-opacity-50${classes}">🇬🇧 LSE (XLON)</span>`;
    }
    if (mic === 'XAMS' || mod === 'euronext_amsterdam') {
      return `<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50${classes}">🇳🇱 Euronext Amsterdam (XAMS)</span>`;
    }
    if (mic === 'XBRU' || mod === 'euronext_brussels') {
      return `<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50${classes}">🇧🇪 Euronext Brussels (XBRU)</span>`;
    }
    if (mic === 'XMAD' || mod === 'bme_madrid') {
      return `<span class="badge bg-warning bg-opacity-25 text-warning border border-warning border-opacity-50${classes}">🇪🇸 BME Madrid (XMAD)</span>`;
    }
    if (mic === 'XMIL' || mod === 'borsa_italiana') {
      return `<span class="badge bg-success bg-opacity-25 text-success border border-success border-opacity-50${classes}">🇮🇹 Borsa Italiana (XMIL)</span>`;
    }
    if (mic === 'XTSE' || mod === 'tsx') {
      return `<span class="badge bg-danger bg-opacity-25 text-danger border border-danger border-opacity-50${classes}">🇨🇦 TSX (XTSE)</span>`;
    }

    if (!mic && !mod && isinCode && isinCode.startsWith('DE')) {
      return `<span class="badge bg-primary bg-opacity-25 text-primary border border-primary border-opacity-50${classes}">🇩🇪 Stuttgart (XSTU)</span>`;
    }

    if (mic) {
      return `<span class="badge bg-secondary font-monospace text-uppercase${classes}">${mic}</span>`;
    }

    if (mod) {
      const displayMod = mod.replace(/_/g, ' ').toUpperCase();
      return `<span class="badge bg-secondary font-monospace${classes}">${displayMod}</span>`;
    }

    return `<span class="badge bg-secondary bg-opacity-25 text-muted border border-secondary border-opacity-50${classes}">Unknown Exchange</span>`;
  },

  /**
   * Shared 3-column reference data detail card generator for Job Verification and Data Explorer.
   */
  renderBondDetailPanel(b, options = {}) {
    const isJobVerify = options.context === 'job_verify';
    const jobId = options.jobId || '';
    const isAdmin = options.isAdmin || (this.currentUser && this.currentUser.role_name === 'admin');

    const safeIsin = sanitize(b.isin);
    const safeDesc1 = sanitize(b.security_desc_1);
    const safeDesc2 = sanitize(b.security_desc_2);
    const safeIssuer = sanitize(b.issuer_name);
    const safeWkn = sanitize((b.wkn && !b.wkn.startsWith('NF')) ? b.wkn : (b.local_code || '—'));
    const safeLocalCode = sanitize(b.local_code);
    const safeMic = sanitize(b.mic_code);
    const targetKey = (b.id && b.id !== 'null' && b.id !== 'undefined') ? b.id : safeIsin;

    let rejectionBanner = '';
    if (b.validation_status === 'rejected') {
      rejectionBanner = `
        <div class="alert bg-danger-light text-danger border border-danger mb-3 py-2 px-3 rounded d-flex align-items-center gap-2 tx-13">
          <i data-feather="alert-octagon" style="width: 18px; height: 18px; flex-shrink: 0;"></i>
          <div>
            <strong>Record Rejected:</strong> "${sanitize(b.validation_note || 'No reason specified')}"
          </div>
        </div>
      `;
    }

    const rowIdAttr = isJobVerify ? `detail-modal-${b.id}` : `detail-${b.id}`;
    const historyIdAttr = isJobVerify ? `history-modal-${b.id}` : `history-${b.id}`;
    const historyBtnClass = isJobVerify ? 'btn-modal-view-history' : 'btn-view-history';
    const exportBtnClass = isJobVerify ? 'btn-modal-export-row' : 'btn-export-row';
    const validateBtnClass = isJobVerify ? 'btn-modal-validate' : 'btn-validate';
    const rejectBtnClass = isJobVerify ? 'btn-toggle-modal-reject' : 'btn-reject';
    const deleteBtnClass = isJobVerify ? 'btn-toggle-modal-delete' : 'btn-delete-bond';

    return `
      <tr class="${isJobVerify ? 'modal-detail-row' : 'detail-row'} d-none bg-dark-card" id="${rowIdAttr}">
        <td colspan="9">
          <div class="p-3 border rounded border-secondary text-start">
            ${rejectionBanner}
            
            <!-- Detail Breakdown Header -->
            <div class="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom border-secondary">
              <div>
                <h6 class="text-white mb-0">${safeDesc1 || safeIsin} Reference Metadata</h6>
                <div class="tx-11 text-muted">Exchange Source: ${safeMic || 'Unspecified'} | Ingested Reference Record ID #${b.id}</div>
              </div>
            </div>

            <div class="row">
              <!-- Column 1: Security & Core Identifiers -->
              <div class="col-lg-4 col-md-6 col-12 mb-3">
                <h6 class="tx-12 text-info uppercase mb-2">1. Security & Core Identifiers</h6>
                <p><strong class="text-muted">ISIN:</strong> <span class="text-white font-monospace">${safeIsin || '—'}</span></p>
                <p><strong class="text-muted">Availability:</strong> <span class="editable-cell text-white" data-field="availability" data-bond-id="${b.id}">${sanitize(b.availability) || 'YES'}</span></p>
                <p><strong class="text-muted">Description 1:</strong> <span class="editable-cell text-white" data-field="security_desc_1" data-bond-id="${b.id}">${safeDesc1 || '—'}</span></p>
                <p><strong class="text-muted">Description 2:</strong> <span class="editable-cell text-white" data-field="security_desc_2" data-bond-id="${b.id}">${safeDesc2 || '—'}</span></p>
                <p><strong class="text-muted">WKN / Valor:</strong> <span class="editable-cell text-white font-monospace" data-field="wkn" data-bond-id="${b.id}">${safeWkn}</span></p>
                <p><strong class="text-muted">Symbol / Local Code:</strong> <span class="editable-cell text-white font-monospace" data-field="local_code" data-bond-id="${b.id}">${safeLocalCode || '—'}</span></p>
                <p><strong class="text-muted">MIC Code:</strong> <span class="text-white font-monospace">${safeMic || '—'}</span></p>
                <p><strong class="text-muted">Exchange Segment:</strong> <span class="editable-cell text-white" data-field="exchange_segment" data-bond-id="${b.id}">${sanitize(b.exchange_segment) || '—'}</span></p>
                <p><strong class="text-muted">Instrument Type:</strong> <span class="editable-cell text-white" data-field="bond_type" data-bond-id="${b.id}">${sanitize(b.bond_type) || '—'}</span></p>
                <p><strong class="text-muted">Sub-type:</strong> <span class="editable-cell text-white" data-field="bond_sub_type" data-bond-id="${b.id}">${sanitize(b.bond_sub_type) || '—'}</span></p>
              </div>

              <!-- Column 2: Issuer & Trading Specifications -->
              <div class="col-lg-4 col-md-6 col-12 mb-3">
                <h6 class="tx-12 text-info uppercase mb-2">2. Issuer & Trading Specifications</h6>
                <p><strong class="text-muted">Issuer Name:</strong> <span class="editable-cell text-white" data-field="issuer_name" data-bond-id="${b.id}">${safeIssuer || '—'}</span></p>
                <p><strong class="text-muted">S&P Rating:</strong> <span class="editable-cell text-white" data-field="sp_rating" data-bond-id="${b.id}">${sanitize(b.sp_rating) || '—'}</span></p>
                <p><strong class="text-muted">Trading Segment / Ticks:</strong> <span class="editable-cell text-white" data-field="trading_segment_ticks" data-bond-id="${b.id}">${sanitize(b.trading_segment_ticks) || '—'}</span></p>
                <p><strong class="text-muted">Trading Hours:</strong> <span class="editable-cell text-white" data-field="trading_hours" data-bond-id="${b.id}">${sanitize(b.trading_hours) || '—'}</span></p>
                <p><strong class="text-muted">Interest Rate (Coupon):</strong> <span class="editable-cell text-white fw-bold" data-field="interest_rate" data-bond-id="${b.id}">${b.interest_rate ? parseFloat(b.interest_rate) + '%' : '—'}</span></p>
                <p><strong class="text-muted">Interest Run From:</strong> <span class="editable-cell text-white" data-field="int_commencement_date" data-bond-id="${b.id}">${b.int_commencement_date ? sanitize(b.int_commencement_date.split('T')[0]) : '—'}</span></p>
                <p><strong class="text-muted">Next Interest Payment:</strong> <span class="editable-cell text-white" data-field="next_interest_payment" data-bond-id="${b.id}">${b.next_interest_payment ? sanitize(b.next_interest_payment.split('T')[0]) : '—'}</span></p>
                <p><strong class="text-muted">Unit Interest:</strong> <span class="editable-cell text-white" data-field="unit_interest" data-bond-id="${b.id}">${sanitize(b.unit_interest) || '—'}</span></p>
                <p><strong class="text-muted">Trading Currency:</strong> <span class="editable-cell text-white font-monospace" data-field="trading_currency" data-bond-id="${b.id}">${sanitize(b.trading_currency) || '—'}</span></p>
                <p><strong class="text-muted">Nominal Currency:</strong> <span class="editable-cell text-white font-monospace" data-field="debt_currency" data-bond-id="${b.id}">${sanitize(b.debt_currency) || '—'}</span></p>
              </div>

              <!-- Column 3: Settlement, Volume & Attributes -->
              <div class="col-lg-4 col-md-12 col-12 mb-3">
                <h6 class="tx-12 text-info uppercase mb-2">3. Settlement & Bond Attributes</h6>
                <p><strong class="text-muted">Settlement Currency:</strong> <span class="editable-cell text-white font-monospace" data-field="settlement_currency" data-bond-id="${b.id}">${sanitize(b.settlement_currency) || '—'}</span></p>
                <p><strong class="text-muted">Quotation:</strong> <span class="editable-cell text-white" data-field="quotation" data-bond-id="${b.id}">${sanitize(b.quotation) || '—'}</span></p>
                <p><strong class="text-muted">Issuing Volume:</strong> <span class="editable-cell text-white" data-field="issue_amount" data-bond-id="${b.id}">${sanitize(b.issue_amount) || '—'}</span></p>
                <p><strong class="text-muted">Minimum Denomination:</strong> <span class="editable-cell text-white" data-field="minimum_denomination" data-bond-id="${b.id}">${sanitize(b.minimum_denomination) || '—'}</span></p>
                <p><strong class="text-muted">Smallest Tradable Unit:</strong> <span class="editable-cell text-white" data-field="min_tradable_amount" data-bond-id="${b.id}">${sanitize(b.min_tradable_amount) || '—'}</span></p>
                <p><strong class="text-muted">Maturity Date:</strong> <span class="editable-cell text-white" data-field="maturity_date" data-bond-id="${b.id}">${b.maturity_date ? sanitize(b.maturity_date.split('T')[0]) : '—'}</span></p>
                <p><strong class="text-muted">Market Maker:</strong> <span class="editable-cell text-white" data-field="market_maker" data-bond-id="${b.id}">${sanitize(b.market_maker) || '—'}</span></p>
                <p><strong class="text-muted">Step Up/Down Rating:</strong> <span class="editable-cell text-white" data-field="step_up_down_rating" data-bond-id="${b.id}">${sanitize(b.step_up_down_rating) || '—'}</span></p>
                <p><strong class="text-muted">Callable / Subordinated:</strong> <span class="text-white">${b.callable ? 'Callable' : 'No'} | ${b.subordinate ? 'Subordinated' : 'Senior'}</span></p>
                <p><strong class="text-muted">FIGI / CFI / LEI:</strong> <span class="text-white font-monospace tx-11">${sanitize(b.figi) || '—'} / ${sanitize(b.cfi_code) || '—'}</span></p>
              </div>
            </div>
            
            <!-- Bottom Verification Action Dashboard -->
            <div class="d-flex justify-content-between align-items-center mt-3 pt-3 border-top" style="border-color: #3f3f46 !important;">
              <div>
                <button class="btn btn-xs btn-outline-secondary ${historyBtnClass}" data-bond-id="${b.id}">Show Edit History</button>
              </div>
              <div class="d-flex gap-2">
                <a href="${b.source_url || '#'}" target="edi_sig_source_verifier" class="btn btn-sm btn-outline-info d-flex align-items-center gap-1" title="Verify Source">
                  <i data-feather="external-link" style="width: 14px; height: 14px;"></i> Check Source
                </a>
                <button class="btn btn-sm btn-outline-secondary ${exportBtnClass} d-flex align-items-center gap-1" data-bond-id="${b.id}" title="Export Row">
                  <i data-feather="download" style="width: 14px; height: 14px;"></i> Export Row
                </button>

                ${isJobVerify ? `
                  <button class="btn btn-sm btn-danger ${rejectBtnClass} d-flex align-items-center gap-1" data-bond-id="${b.id}" title="Reject Record">
                    <i data-feather="x" style="width: 14px; height: 14px;"></i> Reject Record
                  </button>
                  <button class="btn btn-sm btn-success ${validateBtnClass} d-flex align-items-center gap-1" data-bond-id="${b.id}" title="Approve Record">
                    <i data-feather="check" style="width: 14px; height: 14px;"></i> Approve & Verify
                  </button>
                  ${isAdmin ? `<button class="btn btn-sm btn-outline-danger ${deleteBtnClass} d-flex align-items-center gap-1" data-bond-id="${b.id || ''}" data-isin="${b.isin}" title="Permanently Delete Record"><i data-feather="trash-2" style="width: 14px; height: 14px;"></i> Delete Record</button>` : ''}
                ` : `
                  ${isAdmin ? `<button class="btn btn-sm btn-outline-danger ${deleteBtnClass} d-flex align-items-center gap-1" data-bond-id="${b.id || ''}" data-isin="${b.isin}" title="Permanently Delete Record"><i data-feather="trash-2" style="width: 14px; height: 14px;"></i> Delete Record</button>` : ''}
                `}
              </div>
            </div>

            ${isJobVerify ? `
              <!-- Inline Rejection Form Box -->
              <div class="reject-box d-none mt-3 p-3 bg-dark border border-danger rounded text-start" id="reject-box-${targetKey}">
                <label class="form-label text-danger fw-bold tx-12 mb-1">Reason for Rejection / Data Anomaly</label>
                <textarea class="form-control form-control-sm bg-dark text-white border-secondary mb-2" id="reject-reason-${targetKey}" rows="2" placeholder="Enter notes regarding missing fields, incorrect currency, or exchange mismatch..."></textarea>
                <div class="d-flex justify-content-end gap-2">
                  <button class="btn btn-xs btn-secondary btn-cancel-modal-reject" data-bond-id="${b.id || ''}" data-isin="${b.isin}">Cancel</button>
                  <button class="btn btn-xs btn-danger btn-submit-modal-reject" data-bond-id="${b.id || ''}" data-isin="${b.isin}"><i data-feather="x" style="width: 12px; height: 12px;"></i> Confirm Rejection</button>
                </div>
              </div>

              <!-- Inline Delete Confirmation Box -->
              <div class="delete-box d-none mt-3 p-3 bg-dark border border-danger rounded text-start" id="delete-box-${targetKey}">
                <div class="text-danger fw-bold tx-13 mb-1"><i data-feather="alert-triangle" style="width: 14px; height: 14px;"></i> Permanently Delete Reference Record?</div>
                <p class="tx-12 text-muted mb-2">This action cannot be undone and removes ISIN ${safeIsin} permanently from database catalog.</p>
                <div class="d-flex justify-content-end gap-2">
                  <button class="btn btn-xs btn-secondary btn-cancel-modal-delete" data-bond-id="${b.id || ''}" data-isin="${b.isin}">Cancel</button>
                  <button class="btn btn-xs btn-danger btn-submit-modal-delete" data-bond-id="${b.id || ''}" data-isin="${b.isin}"><i data-feather="trash-2" style="width: 12px; height: 12px;"></i> Yes, Delete Record</button>
                </div>
              </div>
            ` : ''}

            <div class="history-log-container d-none mt-2 font-monospace tx-12 text-muted border p-2" id="${historyIdAttr}"></div>
          </div>
        </td>
      </tr>
    `;
  },


  // Alert & Toast Notifications via SweetAlert2
  showToast(icon, title) {
    if (typeof Swal !== 'undefined') {
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
      Toast.fire({ icon, title });
    } else {
      alert(title);
    }
  },

  // API Call Wrapper
  async apiCall(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;
    
    // Add token if exists
    const token = localStorage.getItem('edi_sig_token');
    const headers = { ...options.headers };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (!headers['Content-Type'] && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const fetchOptions = { ...options, headers };

    try {
      const response = await fetch(url, fetchOptions);
      if (response.status === 401) {
        // Session expired or unauthorized
        this.logout();
        throw new Error('Session expired. Please log in again.');
      }
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (err) {
      console.error(`API Call failed to ${endpoint}:`, err);
      throw err;
    }
  },

  // Verify if a scraper task is currently active
  async checkActiveScrapeJob() {
    try {
      const res = await this.apiCall('/jobs');
      const jobs = Array.isArray(res) ? res : (res.jobs || []);
      const activeJob = jobs.find(j => j.status === 'running');
      if (activeJob) {
        this.activeJobId = activeJob.id;
        this.showToast('info', 'Active scraping job detected.');
      }
    } catch (err) {
      console.warn('Scrape status check failed');
    }
  },

  // ---------------------------------------------------------------------------
  // Page: Dashboard Render & Visualizations
  // ---------------------------------------------------------------------------
  async renderDashboard(container) {
    const stats = await this.apiCall('/dashboard/stats').catch(() => ({
      total_isins: 0, validated: 0, rejected: 0, pending: 0, success_rate: 100
    }));

    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0">Operational Telemetry Dashboard</h4>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-xl-12 stretch-card">
          <div class="row flex-grow-1">
            <div class="col-md-3 grid-margin stretch-card">
              <div class="card card-glass cursor-pointer" onclick="window.location.hash = '#/data-explorer?status=all'">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-baseline">
                    <h6 class="card-title mb-0">Total Reference ISINs</h6>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <h3 class="mb-2 text-white">${stats.total_isins}</h3>
                      <p class="text-muted tx-12">Total scraped data points</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3 grid-margin stretch-card">
              <div class="card card-glass cursor-pointer" onclick="window.location.hash = '#/data-explorer?status=validated'">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-baseline">
                    <h6 class="card-title mb-0">Validated Records</h6>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <h3 class="mb-2 text-success">${stats.validated}</h3>
                      <p class="text-muted tx-12">Verified and ready for dispatch</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3 grid-margin stretch-card">
              <div class="card card-glass cursor-pointer" onclick="window.location.hash = '#/data-explorer?status=pending'">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-baseline">
                    <h6 class="card-title mb-0">Pending Audits</h6>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <h3 class="mb-2 text-warning">${stats.pending}</h3>
                      <p class="text-muted tx-12">Scraped rows awaiting validation</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-md-3 grid-margin stretch-card">
              <div class="card card-glass">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-baseline">
                    <h6 class="card-title mb-0">Extraction Success Rate</h6>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <h3 class="mb-2 text-info">${stats.success_rate}%</h3>
                      <p class="text-muted tx-12">Scraping request success metric</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Ingestion Success Overview</h6>
              <div id="dashboardDonutChart" style="min-height: 250px;"></div>
            </div>
          </div>
        </div>
        <div class="col-md-6 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Recent System Activity Logs</h6>
              <div class="table-responsive">
                <table class="table table-hover mb-0">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Origin</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody id="dashboard-recent-events">
                    <tr><td colspan="3" class="text-center text-muted">Loading events...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);

    // Render Recent Audit events in Dashboard
    try {
      const audits = await this.apiCall('/audit');
      const eventTarget = $('#dashboard-recent-events');
      if (audits.length === 0) {
        eventTarget.html('<tr><td colspan="3" class="text-center text-muted">No recent operations logged.</td></tr>');
      } else {
        eventTarget.empty();
        audits.slice(0, 5).forEach(a => {
          eventTarget.append(`
            <tr>
              <td><span class="badge badge-event">${a.event_type}</span></td>
              <td class="text-white">${a.event_source}</td>
              <td class="text-muted">${new Date(a.created_at).toLocaleTimeString()}</td>
            </tr>
          `);
        });
      }
    } catch (e) {
      $('#dashboard-recent-events').html('<tr><td colspan="3" class="text-danger">Failed to fetch activity.</td></tr>');
    }

    // Initialize ApexCharts donut
    if (typeof ApexCharts !== 'undefined') {
      const options = {
        chart: {
          type: 'donut',
          height: 260,
          foreColor: '#71717a'
        },
        series: [stats.validated, stats.pending, stats.rejected],
        labels: ['Validated', 'Pending', 'Rejected'],
        colors: ['#4ade80', '#f59e0b', '#e52e3d'],
        stroke: { colors: ['transparent'] },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              background: 'transparent'
            }
          }
        },
        legend: { position: 'bottom' }
      };
      const chart = new ApexCharts(document.querySelector("#dashboardDonutChart"), options);
      chart.render();
    }
  },

  // ---------------------------------------------------------------------------
  // Page: Ingestion UI (Dropzone and Launch Scraper)
  // ---------------------------------------------------------------------------
  async renderIngestion(container) {
    let parsedISINs = [];
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin mb-4">
        <div>
          <h4 class="mb-1 text-white fw-bold">ISIN Ingestion & Scraper Monitor</h4>
          <p class="text-muted tx-13">Submit ISIN lists, monitor active Playwright scraper workers, and inspect historical execution logs.</p>
        </div>
        <div class="d-flex gap-2 mt-2 mt-sm-0">
          <a href="#/batch-qa" class="btn btn-sm btn-outline-info d-inline-flex align-items-center gap-1 fw-bold">
            <i data-feather="check-square" style="width: 14px; height: 14px;"></i> Open QA Queue (#/batch-qa)
          </a>
        </div>
      </div>

      <!-- Live Active Job Progress Card -->
      <div class="row d-none" id="ingest-active-job-section">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass border-warning">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="card-title text-warning d-flex align-items-center gap-2 mb-0">
                  <span class="spinner-border spinner-border-sm text-warning" role="status"></span>
                  Active Headless Scraping Pool In Progress
                </h6>
              </div>
              <div class="progress mt-3 mb-3" style="height: 15px;">
                <div id="ingest-job-progress-bar" class="progress-bar bg-warning progress-bar-striped progress-bar-animated" role="progressbar" style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">0%</div>
              </div>
              <p class="text-muted tx-13 mb-0">Headless workers are extracting details sequentially using custom anti-fingerprint delay configurations.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Section 1: Upload & Input Card (Side-by-Side Flex Layout) -->
      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              
              <div class="row g-4 align-items-stretch">
                <!-- Left Column: File Dropzone -->
                <div class="col-lg-6 d-flex flex-column">
                  <h6 class="card-title text-info mb-3"><i data-feather="upload-cloud" class="wd-16 ht-16 me-2"></i>Upload Plaintext ISIN File (.txt)</h6>
                  <div class="dropzone flex-grow-1 d-flex align-items-center justify-content-center" id="isin-dropzone" style="cursor: pointer; padding: 30px; text-align: center;">
                    <div class="dz-message">
                      <i class="mb-3 text-muted wd-36 ht-36" data-feather="file-text"></i>
                      <h5 class="tx-15">Drag & drop ISIN list (.txt) or click to upload</h5>
                      <p class="text-muted tx-12 mb-0">Parses valid 12-character ISIN codes automatically.</p>
                    </div>
                  </div>
                </div>

                <!-- Right Column: Manual Entry -->
                <div class="col-lg-6 d-flex flex-column border-start border-secondary ps-lg-4">
                  <h6 class="card-title text-white mb-3"><i data-feather="edit-3" class="wd-16 ht-16 me-2 text-warning"></i>Or Enter ISIN Codes Manually</h6>
                  <div class="mb-3 flex-grow-1">
                    <textarea class="form-control bg-dark border-secondary text-white font-monospace h-100" id="manual-isin-input" rows="4" placeholder="XS3075000862&#10;DE0001041887&#10;CH0441026040" style="line-height: 1.5; letter-spacing: 0.5px; min-height: 110px;"></textarea>
                    <div class="form-text text-danger d-none mt-2" id="isin-validation-warning">
                      <i data-feather="alert-circle" class="wd-14 ht-14 me-1"></i> Some entries are not valid ISIN codes (must be 12 characters starting with a 2-letter country prefix).
                    </div>
                  </div>
                  <div class="d-flex gap-2">
                    <button class="btn btn-outline-primary btn-sm flex-grow-1" id="btn-parse-manual">
                      <i data-feather="check-square" class="wd-14 ht-14 me-1"></i> Parse Manual Entries
                    </button>
                    <button class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1" id="btn-clear-manual" title="Clear Input Text">
                      <i data-feather="trash-2" style="width: 14px; height: 14px;"></i> Clear
                    </button>
                  </div>
                </div>
              </div>

              <!-- Upload Preview & Live Parsed ISIN Stream Drawer -->
              <div id="upload-preview-container" class="mt-4 pt-3 border-top border-secondary d-none">
                <div class="row g-3 align-items-center mb-3">
                  <div class="col-md-5 col-lg-4">
                    <h6 class="text-white mb-0 d-flex align-items-center gap-2">
                      <i data-feather="list" class="wd-16 ht-16 text-info"></i> Parsed ISIN Records (<span id="parsed-count" class="text-info">0</span> found)
                    </h6>
                  </div>
                  <div class="col-md-7 col-lg-8 d-flex justify-content-md-end align-items-center flex-wrap gap-2">
                    <div class="flex-grow-1 flex-md-grow-0" style="min-width: 280px; max-width: 460px;">
                      <select class="form-select form-select-sm bg-dark border-secondary text-white w-100" id="select-exchange-module">
                        <option value="boerse_stuttgart">Loading Modules...</option>
                      </select>
                    </div>
                    <button class="btn btn-sm btn-danger btn-icon-text fw-bold text-nowrap" id="btn-start-scraping">
                      <i class="btn-icon-prepend" data-feather="play"></i> Start Downstream Scraper Pool
                    </button>
                  </div>
                </div>
                <div class="table-responsive border border-secondary rounded scroll-table-container">
                  <table class="table table-hover align-middle tx-13 mb-0" id="parsed-isins-table">
                    <thead class="bg-dark text-white fw-bold" style="position: sticky; top: 0; z-index: 10; background-color: #1e293b !important;">
                      <tr>
                        <th class="text-white fw-bold py-2">#</th>
                        <th class="text-white fw-bold py-2">Target ISIN Code</th>
                        <th class="text-white fw-bold py-2">Deduplication Status</th>
                      </tr>
                    </thead>
                    <tbody id="parsed-isins-body"></tbody>
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <!-- Section 2: Active Headless Scraping Pools Stack (Multi-Job Concurrent Area) -->
      <div class="row mt-4 d-none" id="active-jobs-container">
        <div class="col-md-12">
          <h6 class="text-warning fw-bold mb-3 d-flex align-items-center gap-2">
            <span class="spinner-border spinner-border-sm text-warning" role="status"></span>
            Active Extraction Pools
          </h6>
          <div id="active-jobs-list" class="row"></div>
        </div>
      </div>

      <!-- Section 3: Historical Scraper Execution History & Run Logs -->
      <div class="row mt-4">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h6 class="card-title text-white mb-0"><i data-feather="activity" class="wd-16 ht-16 me-2 text-info"></i>Scraper Execution History & Run Logs</h6>
                <a href="#/batch-qa" class="btn btn-xs btn-outline-info d-inline-flex align-items-center gap-1">
                  <i data-feather="layers" style="width: 12px; height: 12px;"></i> View Batch QA Queue
                </a>
              </div>
              <div class="table-responsive">
                <table class="table table-hover align-middle tx-13" id="ingest-history-table">
                  <thead>
                    <tr>
                      <th>Job UUID</th>
                      <th>Module</th>
                      <th>Status</th>
                      <th>Total ISINs</th>
                      <th>Processed</th>
                      <th>Failures</th>
                      <th>Initiated</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody id="ingest-history-body">
                    <tr><td colspan="8" class="text-center text-muted py-4">Querying execution logs...</td></tr>
                </table>
              </div>

              <!-- History Table Server-Side Pagination Controls -->
              <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-3 border-top border-secondary">
                <div class="tx-12 text-muted" id="ingest-pagination-info">Showing 0 to 0 of 0 runs</div>
                <nav aria-label="Ingest Scraper Runs Pagination">
                  <ul class="pagination pagination-sm mb-0 justify-content-end gap-1" id="ingest-pagination-controls"></ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    `);

    // Unified Polling for active jobs + live ISIN stream + historical logs table
    const self = this;
    this.ingestCurrentPage = 1;

    const pollIngestData = async () => {
      try {
        const page = self.ingestCurrentPage || 1;
        const limit = 10;
        const res = await self.apiCall(`/jobs?page=${page}&limit=${limit}`);
        const jobs = Array.isArray(res) ? res : (res.jobs || []);
        const total = res.total || jobs.length;
        const totalPages = res.totalPages || Math.ceil(total / limit) || 1;
        
        // Save currently open accordion IDs so polling doesn't close them
        const openAccordionIds = new Set();
        $('.accordion-collapse.show').each(function() {
          openAccordionIds.add($(this).attr('id'));
        });

        // Multi-Job Active Pool Stack
        const runningJobs = jobs.filter(j => j.status === 'running');
        if (runningJobs.length > 0) {
          $('#active-jobs-container').removeClass('d-none');
          
          let cardsHtml = '';
          for (const j of runningJobs) {
            const bondsRes = await self.apiCall(`/jobs/${j.id}/bonds?page=1&limit=15`).catch(() => ({ bonds: [], total: 0 }));
            const bonds = Array.isArray(bondsRes) ? bondsRes : (bondsRes.bonds || []);
            const pct = j.total_isins > 0 ? Math.round((j.processed / j.total_isins) * 100) : 0;
            
            let exchangeBadge = self.getExchangeBadge(null, null, j.exchange_module, 'me-2');

            const isinRowsHtml = bonds.map((b, idx) => {
              let isinBadge = '';
              let descText = '';

              if (b.log_status === 'queued') {
                isinBadge = '<span class="badge bg-secondary"><i data-feather="clock" style="width:12px;height:12px;" class="me-1"></i> Queued</span>';
                descText = '<span class="text-muted fst-italic">Queued in worker pool...</span>';
              } else if (b.log_status === 'running') {
                isinBadge = '<span class="badge bg-warning text-dark"><span class="spinner-border spinner-border-sm me-1" role="status" style="width:10px;height:10px;"></span> Running...</span>';
                descText = '<span class="text-warning fst-italic"><span class="spinner-grow spinner-grow-sm me-1" role="status" style="width:8px;height:8px;"></span> Extracting live exchange data...</span>';
              } else if (b.availability === 'NO' || b.log_status === 'no_availability' || (b.error_message && b.error_message.includes('Not Listed'))) {
                isinBadge = '<span class="badge bg-secondary"><i data-feather="slash" style="width:12px;height:12px;" class="me-1"></i> N/A (Not Listed)</span>';
                descText = '<span class="text-muted tx-12 font-monospace">N/A (Not Listed on Exchange)</span>';
              } else if (b.log_status === 'failed') {
                const safeErr = sanitize(b.error_message || 'Extraction failed');
                isinBadge = `<span class="badge bg-danger" title="${safeErr}"><i data-feather="x" style="width:12px;height:12px;" class="me-1"></i> Failed</span>`;
                descText = `<span class="text-danger tx-12">${safeErr}</span>`;
              } else {
                isinBadge = '<span class="badge bg-success"><i data-feather="check" style="width:12px;height:12px;" class="me-1"></i> Processed (Availability: YES)</span>';
                descText = sanitize(b.security_desc_1 || b.issuer_name || '—');
              }

              return `
                <tr>
                  <td class="text-muted py-2">${idx + 1}</td>
                  <td class="font-monospace fw-bold text-white py-2">${sanitize(b.isin) || '—'}</td>
                  <td class="py-2">${isinBadge}</td>
                  <td class="text-white py-2">${descText}</td>
                </tr>
              `;
            }).join('');

            // Keep accordion open by default or if user kept it open
            const collapseId = `collapse-job-${j.id}`;
            const shouldShow = openAccordionIds.size === 0 || openAccordionIds.has(collapseId);

            cardsHtml += `
              <div class="col-md-12 mb-3">
                <div class="card card-glass border-warning">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                      <div>
                        <h6 class="card-title text-warning d-flex align-items-center gap-2 mb-1">
                          <i data-feather="cpu" style="width:16px;height:16px;" class="text-warning"></i>
                          Scraper Pool — <span class="font-monospace text-white">${j.id.slice(0,8)}</span>
                        </h6>
                        <div class="tx-12 text-muted">${exchangeBadge} Initiated: ${new Date(j.created_at).toLocaleTimeString()}</div>
                      </div>
                      <div class="d-flex gap-2">
                        <button class="btn btn-sm btn-outline-danger btn-cancel-job d-inline-flex align-items-center gap-1" data-job-id="${j.id}" title="Cancel Scraping Job">
                          <i data-feather="x-circle" style="width: 14px; height: 14px;"></i> Cancel Job
                        </button>
                        <a href="#/job-verify?jobId=${j.id}" class="btn btn-sm btn-outline-info d-inline-flex align-items-center gap-1">
                          <i data-feather="check-square" style="width: 14px; height: 14px;"></i> Open QA Workspace
                        </a>
                      </div>
                    </div>

                    <div class="progress mt-2 mb-3" style="height: 18px;">
                      <div class="progress-bar bg-warning progress-bar-striped progress-bar-animated text-dark fw-bold" role="progressbar" style="width: ${pct}%;">${pct}% (${j.processed}/${j.total_isins} ISINs)</div>
                    </div>

                    <!-- Collapsible ISIN Live Stream Accordion (Height fit to 4 rows + Light Header) -->
                    <div class="accordion mt-3" id="accordion-job-${j.id}">
                      <div class="accordion-item bg-dark border-secondary">
                        <h2 class="accordion-header">
                          <button class="accordion-button ${shouldShow ? '' : 'collapsed'} bg-dark text-white tx-13 py-2" type="button" data-bs-toggle="collapse" data-bs-target="#${collapseId}">
                            <i data-feather="layers" style="width: 14px; height: 14px;" class="me-2 text-info"></i> Parsed ISIN Live Stream (${bonds.length} Records)
                          </button>
                        </h2>
                        <div id="${collapseId}" class="accordion-collapse collapse ${shouldShow ? 'show' : ''}" data-bs-parent="#accordion-job-${j.id}">
                          <div class="accordion-body p-2">
                            <div class="table-responsive border border-secondary rounded scroll-table-container">
                              <table class="table table-sm table-hover align-middle tx-12 mb-0">
                                <thead class="bg-dark text-white fw-bold" style="position: sticky; top: 0; z-index: 10; background-color: #1e293b !important;">
                                  <tr>
                                    <th class="text-white fw-bold py-2">#</th>
                                    <th class="text-white fw-bold py-2">Target ISIN</th>
                                    <th class="text-white fw-bold py-2">Status</th>
                                    <th class="text-white fw-bold py-2">Description / Security Name</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  ${isinRowsHtml || '<tr><td colspan="4" class="text-center text-muted">Initialising ISIN queue...</td></tr>'}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            `;
          }
          $('#active-jobs-list').html(cardsHtml);
        } else {
          $('#active-jobs-container').addClass('d-none');
        }

        // History logs table
        const tbody = $('#ingest-history-body');
        if (jobs.length === 0) {
          tbody.html('<tr><td colspan="8" class="text-center text-muted py-4">No scraper execution runs found.</td></tr>');
          return;
        }

        tbody.empty();
        jobs.forEach(j => {
          let statusBadge = '';
          if (j.status === 'running') statusBadge = '<span class="badge bg-warning text-dark">Running</span>';
          else if (j.status === 'dispatched' || j.last_dispatched_at) statusBadge = '<span class="badge bg-primary">Dispatched</span>';
          else if (j.status === 'completed' || j.status === 'finished' || (j.processed > 0 && j.failed_count === 0)) statusBadge = '<span class="badge bg-success">Completed</span>';
          else if (j.status === 'cancelled') statusBadge = '<span class="badge bg-secondary">Cancelled</span>';
          else statusBadge = '<span class="badge bg-danger">Failed</span>';

          tbody.append(`
            <tr>
              <td class="font-monospace fw-bold"><a href="#/job-verify?jobId=${j.id}" class="text-info">${j.id.slice(0, 8)}...</a></td>
              <td>${self.getExchangeBadge(null, null, j.exchange_module)}</td>
              <td>${statusBadge}</td>
              <td class="text-white fw-bold">${j.total_isins}</td>
              <td class="text-success fw-bold">${j.processed}</td>
              <td class="text-danger fw-bold">${j.failed_count}</td>
              <td class="text-muted tx-12">${new Date(j.created_at).toLocaleString()}</td>
              <td class="text-muted tx-12">${j.completed_at ? new Date(j.completed_at).toLocaleString() : '—'}</td>
            </tr>
          `);
        });

        // Update Ingest History Pagination Info & Controls
        const startIdx = total === 0 ? 0 : (page - 1) * limit + 1;
        const endIdx = Math.min(page * limit, total);
        $('#ingest-pagination-info').text(`Showing ${startIdx} to ${endIdx} of ${total} scraper runs`);

        const pagControls = $('#ingest-pagination-controls').empty();

        // Prev Button
        const prevDisabled = (page <= 1) ? 'disabled' : '';
        pagControls.append(`
          <li class="page-item ${prevDisabled}">
            <a class="page-link ingest-page-link bg-dark text-white border-secondary" href="#" data-page="${page - 1}" aria-label="Previous">
              <span aria-hidden="true">&laquo;</span>
            </a>
          </li>
        `);

        // Smart Ellipsis Page Numbers Renderer (e.g. 1 ... 4 5 6 ... 20)
        const pagesToRender = [];
        const delta = 1;

        for (let p = 1; p <= totalPages; p++) {
          if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
            pagesToRender.push(p);
          } else if (pagesToRender[pagesToRender.length - 1] !== '...') {
            pagesToRender.push('...');
          }
        }

        pagesToRender.forEach(pItem => {
          if (pItem === '...') {
            pagControls.append(`
              <li class="page-item disabled">
                <span class="page-link bg-dark text-muted border-secondary px-2">...</span>
              </li>
            `);
          } else {
            const activeClass = (pItem === page) ? 'active bg-primary text-white border-primary' : 'bg-dark text-white border-secondary';
            pagControls.append(`
              <li class="page-item ${pItem === page ? 'active' : ''}">
                <a class="page-link ingest-page-link ${activeClass}" href="#" data-page="${pItem}">${pItem}</a>
              </li>
            `);
          }
        });

        // Next Button
        const nextDisabled = (page >= totalPages) ? 'disabled' : '';
        pagControls.append(`
          <li class="page-item ${nextDisabled}">
            <a class="page-link ingest-page-link bg-dark text-white border-secondary" href="#" data-page="${page + 1}" aria-label="Next">
              <span aria-hidden="true">&raquo;</span>
            </a>
          </li>
        `);

        if (typeof feather !== 'undefined') feather.replace();

      } catch (e) {
        console.warn('Ingest polling error:', e);
      }
    };

    // Ingest History Pagination Click Listener
    $(document).off('click', '.ingest-page-link').on('click', '.ingest-page-link', function(e) {
      e.preventDefault();
      const targetPage = parseInt($(this).attr('data-page'), 10);
      if (targetPage && targetPage !== self.ingestCurrentPage && targetPage > 0) {
        self.ingestCurrentPage = targetPage;
        pollIngestData();
      }
    });

    pollIngestData();
    this.pollingInterval = setInterval(pollIngestData, 2000);

    // Cancel Job Click Handler
    $(document).off('click', '.btn-cancel-job').on('click', '.btn-cancel-job', async function() {
      const jobId = $(this).attr('data-job-id');
      const confirm = await Swal.fire({
        title: 'Cancel Extraction Job?',
        text: `Are you sure you want to cancel Job #${jobId.substring(0,8)}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        confirmButtonText: 'Yes, Cancel Job'
      });
      if (confirm.isConfirmed) {
        try {
          await self.apiCall(`/jobs/${jobId}/cancel`, { method: 'POST' });
          self.showToast('warning', `Job #${jobId.substring(0,8)} cancelled.`);
          pollIngestData();
        } catch (err) {
          self.showToast('error', err.message);
        }
      }
    });

    // Populate exchange modules dropdown with full registry
    // Only Börse Stuttgart is active in the current PoC; others show the multi-exchange vision
    const exchangeRegistry = [
      // ── Active Exchanges ──
      { group: '🟢 Active Exchanges', items: [
        { name: 'boerse_stuttgart', label: '🇩🇪 Börse Stuttgart', active: true },
      ]},
      // ── European Exchanges ──
      { group: '🇪🇺 European Exchanges', items: [
        { name: 'six_swiss',          label: '🇨🇭 SIX Swiss Exchange (Switzerland)' },
        { name: 'boerse_frankfurt',    label: '🇩🇪 Frankfurt Stock Exchange / Börse Frankfurt (Germany)' },
        { name: 'euronext_paris',      label: '🇫🇷 Euronext Paris (France)' },
        { name: 'euronext_milan',      label: '🇮🇹 Euronext Milan (Italy)' },
        { name: 'euronext_lisbon',     label: '🇵🇹 Euronext Lisbon (Portugal)' },
      ]},
      // ── Central Banks & Treasuries ──
      { group: '🏛️ Central Banks & Treasuries', items: [
        { name: 'snb_swiss',           label: '🇨🇭 Swiss National Bank — SNB (Switzerland)' },
        { name: 'austria_treasury',    label: '🇦🇹 Austrian Treasury (Austria)' },
        { name: 'oekb_austria',        label: '🇦🇹 OeKB — Oesterreichische Kontrollbank (Austria)' },
        { name: 'bundesbank',          label: '🇩🇪 Deutsche Bundesbank (Germany)' },
        { name: 'spain_treasury',      label: '🇪🇸 Spain Treasury (Spain)' },
      ]},
      // ── Latin American Exchanges ──
      { group: '🌎 Latin American Exchanges', items: [
        { name: 'latinex_panama',      label: '🇵🇦 Latinex (Panama)' },
        { name: 'bvpasa_paraguay',     label: '🇵🇾 Asunción Stock Exchange / BVPASA (Paraguay)' },
        { name: 'bevsa_uruguay',       label: '🇺🇾 BEVSA — Bolsa Electrónica de Valores (Uruguay)' },
        { name: 'bcu_uruguay',         label: '🇺🇾 Uruguay Central Bank (Uruguay)' },
        { name: 'chile_treasury',      label: '🇨🇱 Chile Treasury (Chile)' },
        { name: 'cevaldom_dr',         label: '🇩🇴 CEVALDOM (Dominican Republic)' },
        { name: 'dr_central_bank',     label: '🇩🇴 Dominican Republic Central Bank' },
        { name: 'banxico_mexico',      label: '🇲🇽 Banco de México (Mexico)' },
        { name: 'nicaragua_cb',        label: '🇳🇮 Nicaragua Central Bank (Nicaragua)' },
      ]},
    ];

    const select = $('#select-exchange-module').empty();
    exchangeRegistry.forEach(group => {
      const optgroup = $(`<optgroup label="${group.group}"></optgroup>`);
      group.items.forEach(ex => {
        const opt = $(`<option value="${ex.name}">${ex.label}${ex.active ? '' : ' — Coming Soon'}</option>`);
        if (!ex.active) opt.attr('disabled', true);
        optgroup.append(opt);
      });
      select.append(optgroup);
    });

    // Live textarea validator
    $('#manual-isin-input').on('input', function() {
      const text = $(this).val().trim();
      if (!text) {
        $('#isin-validation-warning').addClass('d-none');
        return;
      }
      
      const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
      const invalid = lines.some(l => !/^[A-Z]{2}[A-Z0-9]{10}$/i.test(l));
      
      if (invalid) {
        $('#isin-validation-warning').removeClass('d-none');
      } else {
        $('#isin-validation-warning').addClass('d-none');
      }
      
      if (typeof feather !== 'undefined') {
        feather.replace();
      }
    });

    // Helper to render Parsed ISIN Records Preview Table with Duplicate Highlighting
    const renderParsedISINsTable = (rawList) => {
      const seen = new Set();
      const tbody = $('#parsed-isins-body').empty();
      
      rawList.forEach((isin, idx) => {
        const isDuplicate = seen.has(isin);
        seen.add(isin);
        
        let statusBadge = isDuplicate 
          ? '<span class="badge bg-warning text-dark"><i data-feather="alert-triangle" style="width:12px;height:12px;" class="me-1"></i> Duplicate — will be deduplicated</span>' 
          : '<span class="badge bg-success"><i data-feather="check" style="width:12px;height:12px;" class="me-1"></i> Unique (Queued)</span>';
          
        let rowStyle = isDuplicate ? 'style="background-color: rgba(245, 158, 11, 0.12);"' : '';
        
        tbody.append(`
          <tr ${rowStyle}>
            <td class="text-muted py-2">${idx + 1}</td>
            <td class="text-white font-monospace fw-bold py-2">${isin}</td>
            <td class="py-2">${statusBadge}</td>
          </tr>
        `);
      });
      if (typeof feather !== 'undefined') feather.replace();
    };

    // Manual ISIN parser button trigger
    $('#btn-parse-manual').on('click', () => {
      const text = $('#manual-isin-input').val().trim();
      if (!text) {
        self.showToast('warning', 'Please enter at least one ISIN.');
        return;
      }
      
      const lines = text.split('\n').map(l => l.trim().toUpperCase()).filter(l => l.length > 0);
      const rawISINs = lines.filter(l => /^[A-Z]{2}[A-Z0-9]{10}$/.test(l));
      
      if (rawISINs.length === 0) {
        self.showToast('error', 'No valid ISIN codes found (must be 12 characters starting with a 2-letter country prefix).');
        return;
      }
      
      // Deduplicate ISINs to prevent redundant scraping
      const uniqueISINs = [...new Set(rawISINs)];
      const duplicatesCount = rawISINs.length - uniqueISINs.length;
      
      const MAX_BATCH_SIZE = 1000;
      if (uniqueISINs.length > MAX_BATCH_SIZE) {
        self.showToast('warning', `Input contains ${uniqueISINs.length} ISINs. Truncated to maximum allowed limit of ${MAX_BATCH_SIZE} ISINs per job.`);
        parsedISINs = uniqueISINs.slice(0, MAX_BATCH_SIZE);
      } else {
        parsedISINs = uniqueISINs;
      }
      
      $('#parsed-count').text(parsedISINs.length);
      renderParsedISINsTable(rawISINs);
      
      $('#upload-preview-container').removeClass('d-none');
      const dupeNotice = duplicatesCount > 0 ? ` (${duplicatesCount} duplicate(s) highlighted & auto-removed)` : '';
      self.showToast('success', `${parsedISINs.length} unique ISINs parsed successfully${dupeNotice}.`);
      if (typeof feather !== 'undefined') feather.replace();
    });

    // Clear manual ISIN textarea button
    $('#btn-clear-manual').on('click', () => {
      $('#manual-isin-input').val('');
      $('#isin-validation-warning').addClass('d-none');
      self.showToast('info', 'Manual ISIN input cleared.');
    });
    // Common file upload processor for click and drag-and-drop
    const processUploadFile = async (file) => {
      if (!file) return;
      if (!file.name.endsWith('.txt')) {
        self.showToast('error', 'Please upload a plain text (.txt) ISIN list file.');
        return;
      }
      
      const $dzMsg = $dropzone.find('.dz-message');
      $dzMsg.html(`
        <div class="spinner-border text-info mb-3" role="status" style="width: 2.2rem; height: 2.2rem;"></div>
        <h5 class="tx-15 text-info fw-bold">Parsing & Uploading ISIN File...</h5>
        <p class="text-muted tx-12 mb-0">Processing ${file.name}...</p>
      `);
      
      try {
        const text = await file.text();
        const lines = text.split('\n').map(l => l.trim().toUpperCase()).filter(l => l.length > 0);
        
        if (lines.length === 0) {
          self.showToast('error', `File "${file.name}" is completely empty. Please select a valid .txt file.`);
          return;
        }

        const rawISINs = lines.filter(l => /^[A-Z]{2}[A-Z0-9]{10}$/.test(l));
        if (rawISINs.length === 0) {
          self.showToast('error', `File "${file.name}" contained 0 valid ISIN codes. Each line must be a 12-character code starting with a 2-letter country prefix (e.g. DE0001041887).`);
          return;
        }

        const uniqueISINs = [...new Set(rawISINs)];
        const duplicatesCount = rawISINs.length - uniqueISINs.length;

        const MAX_BATCH_SIZE = 1000;
        if (uniqueISINs.length > MAX_BATCH_SIZE) {
          self.showToast('warning', `File contains ${uniqueISINs.length} ISINs. Truncated to maximum allowed limit of ${MAX_BATCH_SIZE} ISINs per job.`);
          parsedISINs = uniqueISINs.slice(0, MAX_BATCH_SIZE);
        } else {
          parsedISINs = uniqueISINs;
        }

        // Upload file to backend for audit logging
        const formData = new FormData();
        formData.append('file', file);
        await self.apiCall('/upload', {
          method: 'POST',
          body: formData
        });
        
        $('#parsed-count').text(parsedISINs.length);
        renderParsedISINsTable(rawISINs);
        
        $('#upload-preview-container').removeClass('d-none');
        const dupeNotice = duplicatesCount > 0 ? ` (${duplicatesCount} duplicate(s) highlighted & auto-removed)` : '';
        self.showToast('success', `${parsedISINs.length} unique ISINs parsed from ${file.name}${dupeNotice}.`);
        
        if (typeof feather !== 'undefined') {
          feather.replace();
        }
      } catch (err) {
        self.showToast('error', err.message || 'Failed to upload ISINs file.');
      } finally {
        $dzMsg.html(defaultDzHtml);
        if (typeof feather !== 'undefined') feather.replace();
      }
    };

    // Standard click file browser trigger
    $('#isin-dropzone').on('click', (e) => {
      // Prevent click loop if triggered on child elements
      if ($(e.target).is('input[type="file"]')) return;
      const input = $('<input type="file" accept=".txt">');
      input.on('change', async (evt) => {
        const file = evt.target.files[0];
        await processUploadFile(file);
      });
      input.click();
    });

    // Native HTML5 Drag & Drop Handlers with Visual Feedback
    const $dropzone = $('#isin-dropzone');
    const defaultDzHtml = `
      <i class="mb-3 text-muted wd-36 ht-36" data-feather="file-text"></i>
      <h5 class="tx-15">Drag & drop ISIN list (.txt) or click to upload</h5>
      <p class="text-muted tx-12 mb-0">Parses valid 12-character ISIN codes automatically.</p>
    `;
    const activeDzHtml = `
      <i class="mb-3 text-warning wd-36 ht-36" data-feather="upload-cloud"></i>
      <h5 class="tx-15 text-warning fw-bold">Release file to upload ISIN list (.txt)</h5>
      <p class="text-warning tx-12 mb-0 opacity-75">Ready to parse valid ISIN codes.</p>
    `;

    $dropzone.on('dragover dragenter', (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!$dropzone.hasClass('dz-drag-hover')) {
        $dropzone.addClass('dz-drag-hover');
        $dropzone.find('.dz-message').html(activeDzHtml);
        if (typeof feather !== 'undefined') feather.replace();
      }
    });

    $dropzone.on('dragleave dragend', (e) => {
      e.preventDefault();
      e.stopPropagation();
      $dropzone.removeClass('dz-drag-hover');
      $dropzone.find('.dz-message').html(defaultDzHtml);
      if (typeof feather !== 'undefined') feather.replace();
    });

    $dropzone.on('drop', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      $dropzone.removeClass('dz-drag-hover');
      $dropzone.find('.dz-message').html(defaultDzHtml);
      if (typeof feather !== 'undefined') feather.replace();
      
      const files = e.originalEvent.dataTransfer ? e.originalEvent.dataTransfer.files : null;
      if (files && files.length > 0) {
        await processUploadFile(files[0]);
      }
    });

    $('#btn-start-scraping').on('click', async () => {
      if (parsedISINs.length === 0) return;
      
      const moduleName = $('#select-exchange-module').val() || 'boerse_stuttgart';
      
      const confirm = await Swal.fire({
        title: 'Launch Downstream Scraper Pool?',
        text: `This will spawn headless Playwright workers to extract details for ${parsedISINs.length} ISIN codes using the '${moduleName}' module.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a',
        confirmButtonText: 'Execute scraping'
      });
      
      if (confirm.isConfirmed) {
        const $btn = $('#btn-start-scraping');
        const origHtml = $btn.html();
        $btn.prop('disabled', true).html('<span class="spinner-border spinner-border-sm me-1" role="status"></span> Launching...');
        
        try {
          const res = await self.apiCall('/jobs', {
            method: 'POST',
            body: JSON.stringify({ isins: parsedISINs, module: moduleName })
          });
          self.activeJobId = res.job_id;
          self.showToast('success', 'Scraper pool launched successfully.');
          
          // Reset Upload Input Card for new job submissions
          $('#upload-preview-container').addClass('d-none');
          $('#manual-isin-input').val('');
          parsedISINs = [];
          $('#parsed-count').text(0);
          $('#parsed-isins-body').empty();

          pollIngestData();
        } catch (err) {
          self.showToast('error', err.message || 'Scraper ignition failed.');
        } finally {
          $btn.prop('disabled', false).html(origHtml);
        }
      }
    });
  },

  // ---------------------------------------------------------------------------
  // Page: Ingestion QA & Verification Queue (#/batch-qa)
  // ---------------------------------------------------------------------------
  async renderBatchQA(container) {
    const self = this;
    
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin mb-4">
        <div>
          <h4 class="mb-1 text-white fw-bold">Ingestion QA & Verification Queue</h4>
          <p class="text-muted tx-13">Inspect, inline-correct, approve, and dispatch job-specific Excel feeds to client SFTP destinations.</p>
        </div>
        <div>
          <a href="#/ingest" class="btn btn-sm btn-primary fw-bold">
            <i data-feather="upload-cloud" class="wd-14 ht-14 me-1"></i> + Ingest New ISIN File
          </a>
        </div>
      </div>

      <!-- Summary KPI Metric Cards -->
      <div class="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-5 g-3 mb-4">
        <div class="col">
          <div class="card card-glass h-100">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-11 text-uppercase font-monospace mb-1">Total Batches</div>
                <h3 class="text-white fw-bold mb-0" id="bqa-stat-total">—</h3>
              </div>
              <div class="wd-38 ht-38 rounded-circle bg-primary bg-opacity-25 d-flex align-items-center justify-content-center text-primary">
                <i data-feather="layers" class="wd-18 ht-18"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="col">
          <div class="card card-glass border-start border-warning border-3 h-100">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-11 text-uppercase font-monospace mb-1">Scraping Active</div>
                <h3 class="text-warning fw-bold mb-0" id="bqa-stat-running">—</h3>
              </div>
              <div class="wd-38 ht-38 rounded-circle bg-warning bg-opacity-25 d-flex align-items-center justify-content-center text-warning">
                <i data-feather="play-circle" class="wd-18 ht-18"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="col">
          <div class="card card-glass border-start border-info border-3 h-100">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-11 text-uppercase font-monospace mb-1">Pending Review</div>
                <h3 class="text-info fw-bold mb-0" id="bqa-stat-pending">—</h3>
              </div>
              <div class="wd-38 ht-38 rounded-circle bg-info bg-opacity-25 d-flex align-items-center justify-content-center text-info">
                <i data-feather="clock" class="wd-18 ht-18"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="col">
          <div class="card card-glass border-start border-success border-3 h-100">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-11 text-uppercase font-monospace mb-1">Fully Verified</div>
                <h3 class="text-success fw-bold mb-0" id="bqa-stat-verified">—</h3>
              </div>
              <div class="wd-38 ht-38 rounded-circle bg-success bg-opacity-25 d-flex align-items-center justify-content-center text-success">
                <i data-feather="check-circle" class="wd-18 ht-18"></i>
              </div>
            </div>
          </div>
        </div>

        <div class="col">
          <div class="card card-glass border-start border-primary border-3 h-100">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-11 text-uppercase font-monospace mb-1">Completed Jobs</div>
                <h3 class="text-white fw-bold mb-0" id="bqa-stat-dispatched">—</h3>
              </div>
              <div class="wd-38 ht-38 rounded-circle bg-info bg-opacity-25 d-flex align-items-center justify-content-center text-info">
                <i data-feather="send" class="wd-18 ht-18"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Batch Ingestion Queue Table -->
      <div class="row">
        <div class="col-md-12">
          <div class="card card-glass">
            <div class="card-body">

              <!-- Status Filter Pill Tabs -->
              <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 pb-3 border-bottom border-secondary border-opacity-25">
                <div class="nav nav-pills gap-2" id="qa-status-pills">
                  <button class="nav-link btn btn-sm px-3 py-1 btn-primary active qa-status-tab" data-status="all">
                    <i data-feather="grid" style="width:12px;height:12px;" class="me-1"></i> All Batches
                  </button>
                  <button class="nav-link btn btn-sm px-3 py-1 btn-outline-secondary text-white qa-status-tab" data-status="running">
                    <i data-feather="play-circle" style="width:12px;height:12px;" class="me-1 text-warning"></i> Running
                  </button>
                  <button class="nav-link btn btn-sm px-3 py-1 btn-outline-secondary text-white qa-status-tab" data-status="in_review">
                    <i data-feather="clock" style="width:12px;height:12px;" class="me-1 text-info"></i> In Review
                  </button>
                  <button class="nav-link btn btn-sm px-3 py-1 btn-outline-secondary text-white qa-status-tab" data-status="verified">
                    <i data-feather="check-circle" style="width:12px;height:12px;" class="me-1 text-success"></i> Fully Verified
                  </button>
                  <button class="nav-link btn btn-sm px-3 py-1 btn-outline-secondary text-white qa-status-tab" data-status="dispatched">
                    <i data-feather="send" style="width:12px;height:12px;" class="me-1 text-primary"></i> Dispatched / Completed
                  </button>
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <h6 class="card-title text-white mb-0 me-2"><i data-feather="layers" class="wd-16 ht-16 me-2 text-warning"></i>Uploaded File Batches & Verification Queue</h6>
                  
                  <!-- Multi-Select Bulk Actions Bar -->
                  <div class="d-none align-items-center gap-2 flex-wrap" id="qa-bulk-actions-bar">
                    <button class="btn btn-sm btn-success d-inline-flex align-items-center gap-1" id="btn-qa-bulk-validate">
                      <i data-feather="check-circle" style="width:14px;height:14px;"></i>
                      <span>Validate Selected (<span class="qa-selected-count">0</span>)</span>
                    </button>

                    <div class="dropup d-inline-block">
                      <button class="btn btn-sm btn-outline-secondary dropdown-toggle d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport" aria-expanded="false">
                        <i data-feather="download" style="width: 14px; height: 14px;"></i> Export Selected (<span class="qa-selected-count">0</span>)
                      </button>
                      <ul class="dropdown-menu shadow border-0 rounded-3 py-1">
                        <li>
                          <a class="dropdown-item btn-qa-bulk-export text-dark d-flex align-items-center gap-2 tx-12 py-2" href="#" data-type="full">
                            <i data-feather="file-text" style="width:14px;height:14px;" class="text-info"></i>
                            <span class="fw-semibold">Full Selected Batches (.zip/.xlsx)</span>
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item btn-qa-bulk-export text-dark d-flex align-items-center gap-2 tx-12 py-2" href="#" data-type="validated">
                            <i data-feather="check-circle" style="width:14px;height:14px;" class="text-success"></i>
                            <span class="fw-semibold">Validated Records Only (.zip/.xlsx)</span>
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item btn-qa-bulk-export text-dark d-flex align-items-center gap-2 tx-12 py-2" href="#" data-type="rejected">
                            <i data-feather="x-circle" style="width:14px;height:14px;" class="text-danger"></i>
                            <span class="fw-semibold">Rejected Exceptions Only (.zip/.xlsx)</span>
                          </a>
                        </li>
                      </ul>
                    </div>

                    <button class="btn btn-sm btn-warning text-dark d-inline-flex align-items-center gap-1" id="btn-qa-bulk-sftp">
                      <i data-feather="send" style="width:14px;height:14px;"></i>
                      <span>Send SFTP (<span class="qa-selected-count">0</span>)</span>
                    </button>

                    <button class="btn btn-sm btn-danger d-inline-flex align-items-center gap-1" id="btn-qa-bulk-delete">
                      <i data-feather="trash-2" style="width:14px;height:14px;"></i>
                      <span>Delete Selected (<span class="qa-selected-count">0</span>)</span>
                    </button>
                  </div>
                </div>
                
                <div class="d-flex align-items-center gap-3">
                  <div class="d-flex align-items-center gap-2">
                    <label class="tx-12 text-muted mb-0">Show</label>
                    <select class="form-select form-select-sm bg-dark text-white border-secondary w-auto py-1" id="qa-page-limit">
                      <option value="10" selected>10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                    <span class="tx-12 text-muted">per page</span>
                  </div>

                  <div class="input-group input-group-sm" style="width: 240px;">
                    <span class="input-group-text bg-dark border-secondary text-muted"><i data-feather="search" style="width:14px;height:14px;"></i></span>
                    <input type="text" class="form-control form-control-sm bg-dark text-white border-secondary" id="qa-search-input" placeholder="Search Job ID or Exchange...">
                  </div>
                </div>
              </div>
              
              <div class="table-responsive">
                <table class="table table-hover align-middle tx-13" id="batch-qa-table">
                  <thead>
                    <tr>
                      <th style="width: 38px;">
                        <input type="checkbox" class="form-check-input cursor-pointer" id="qa-select-all" title="Select All">
                      </th>
                      <th>Batch Job ID</th>
                      <th class="qa-sortable-th cursor-pointer text-nowrap" data-sort-col="exchange_module" title="Click to Sort by Exchange">
                        Exchange Platform <span class="sort-icon tx-11 ms-1 text-muted" id="sort-icon-exchange_module">↕</span>
                      </th>
                      <th class="qa-sortable-th cursor-pointer text-nowrap" data-sort-col="total_isins" title="Click to Sort by Total ISINs">
                        Total ISINs <span class="sort-icon tx-11 ms-1 text-muted" id="sort-icon-total_isins">↕</span>
                      </th>
                      <th>QA Review Progress</th>
                      <th class="qa-sortable-th cursor-pointer text-nowrap" data-sort-col="status" title="Click to Sort by Status">
                        Batch Status <span class="sort-icon tx-11 ms-1 text-muted" id="sort-icon-status">↕</span>
                      </th>
                      <th class="qa-sortable-th cursor-pointer text-nowrap" data-sort-col="created_at" title="Click to Sort by Created Date">
                        Created At <span class="sort-icon tx-11 ms-1 text-info fw-bold" id="sort-icon-created_at">▼</span>
                      </th>
                      <th>Last Dispatched</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody id="batch-qa-body">
                    <tr><td colspan="9" class="text-center text-muted py-4">Querying batch queue...</td></tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination Footer Controls -->
              <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2 pt-2 border-top border-secondary border-opacity-25">
                <div class="tx-12 text-muted" id="qa-pagination-info">Showing 0 to 0 of 0 batch jobs</div>
                <nav aria-label="Batch Queue Navigation">
                  <ul class="pagination pagination-sm mb-0 justify-content-end gap-1" id="qa-pagination-controls">
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    `);

    if (typeof feather !== 'undefined') feather.replace();

    // State properties for pagination, status, sorting & search
    this.qaCurrentPage = 1;
    this.qaLimit = 10;
    this.qaSearch = '';
    this.qaStatus = 'all';
    this.qaSortBy = 'created_at';
    this.qaSortOrder = 'desc';

    // Clickable Column Header Sort Handler
    $('.qa-sortable-th').off('click').on('click', function() {
      const col = $(this).attr('data-sort-col');
      if (self.qaSortBy === col) {
        self.qaSortOrder = self.qaSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        self.qaSortBy = col;
        self.qaSortOrder = 'asc';
      }
      self.qaCurrentPage = 1;
      self.refreshBatchQAPage();
    });

    // Status Tab Filter Click Handler
    $('.qa-status-tab').off('click').on('click', function(e) {
      e.preventDefault();
      $('.qa-status-tab').removeClass('active btn-primary').addClass('btn-outline-secondary text-white');
      $(this).removeClass('btn-outline-secondary text-white').addClass('active btn-primary');
      self.qaStatus = $(this).attr('data-status');
      self.qaCurrentPage = 1;
      self.refreshBatchQAPage();
    });

    // Page Limit Change Handler
    $('#qa-page-limit').off('change').on('change', function() {
      self.qaLimit = parseInt($(this).val(), 10);
      self.qaCurrentPage = 1;
      self.refreshBatchQAPage();
    });

    // Search Input Handler with Debounce
    let searchDebounce;
    $('#qa-search-input').off('keyup input').on('keyup input', function() {
      clearTimeout(searchDebounce);
      const val = $(this).val();
      searchDebounce = setTimeout(() => {
        self.qaSearch = val;
        self.qaCurrentPage = 1;
        self.refreshBatchQAPage();
      }, 300);
    });

    // Pagination Click Handler
    $(document).off('click', '.qa-page-link').on('click', '.qa-page-link', function(e) {
      e.preventDefault();
      const targetPage = parseInt($(this).attr('data-page'), 10);
      if (targetPage && targetPage !== self.qaCurrentPage) {
        self.qaCurrentPage = targetPage;
        self.refreshBatchQAPage();
      }
    });

    // Select All Checkbox Handler
    $(document).off('change', '#qa-select-all').on('change', '#qa-select-all', function() {
      const isChecked = $(this).is(':checked');
      $('.qa-job-checkbox').prop('checked', isChecked);
      self.updateBulkDeleteVisibility();
    });

    // Individual Row Checkbox Handler
    $(document).off('change', '.qa-job-checkbox').on('change', '.qa-job-checkbox', function() {
      const totalCheckboxes = $('.qa-job-checkbox').length;
      const checkedCheckboxes = $('.qa-job-checkbox:checked').length;
      $('#qa-select-all').prop('checked', totalCheckboxes > 0 && totalCheckboxes === checkedCheckboxes);
      self.updateBulkDeleteVisibility();
    });

    // Bulk Validate Click Handler
    $(document).off('click', '#btn-qa-bulk-validate').on('click', '#btn-qa-bulk-validate', async function() {
      const selectedJobIds = self.getSelectedJobIds();
      if (selectedJobIds.length === 0) return;

      const { isConfirmed } = await Swal.fire({
        title: `Validate ${selectedJobIds.length} Selected Batches?`,
        text: `Approve and mark all pending ISIN records as Validated across ${selectedJobIds.length} batch job(s)?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#10b981',
        cancelButtonColor: '#71717a',
        confirmButtonText: `Yes, Validate ${selectedJobIds.length} Batches`
      });

      if (isConfirmed) {
        try {
          const res = await self.apiCall('/jobs/bulk-validate', {
            method: 'POST',
            body: JSON.stringify({ jobIds: selectedJobIds })
          });
          self.showToast('success', res.message);
          await self.refreshBatchQAPage();
        } catch (err) {
          self.showToast('error', err.message || 'Bulk validation failed.');
        }
      }
    });

    $(document).off('click', '.btn-qa-bulk-export').on('click', '.btn-qa-bulk-export', async function(e) {
      e.preventDefault();
      const selectedJobIds = self.getSelectedJobIds();
      if (selectedJobIds.length === 0) return;
      const exportType = $(this).attr('data-type');
      const token = localStorage.getItem('edi_sig_token');
      try {
        self.showToast('info', 'Preparing bulk export...');
        const url = `${API_BASE}/jobs/bulk-export?jobIds=${selectedJobIds.join(',')}&type=${exportType}&_t=${Date.now()}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Bulk export failed.');
        }
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = selectedJobIds.length === 1 ? `job_${selectedJobIds[0]}_${exportType}.xlsx` : `bulk_jobs_${exportType}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        self.showToast('error', err.message || 'Export failed.');
      }
    });

    // Bulk SFTP Dispatch Click Handler
    $(document).off('click', '#btn-qa-bulk-sftp').on('click', '#btn-qa-bulk-sftp', async function() {
      const selectedJobIds = self.getSelectedJobIds();
      if (selectedJobIds.length === 0) return;

      try {
        const dests = await self.apiCall('/ftp/destinations');
        if (dests.length === 0) {
          self.showToast('error', 'Please configure an SFTP destination in Settings first.');
          return;
        }
        const inputOptions = {};
        dests.forEach(d => { inputOptions[d.id] = `${d.name} (${d.host})`; });

        const { value: destId } = await Swal.fire({
          title: `Dispatch ${selectedJobIds.length} Batch Excel Files`,
          text: `Send validated Excel feeds for ${selectedJobIds.length} verified batch job(s) to target SFTP server:`,
          input: 'select',
          inputOptions,
          showCancelButton: true,
          confirmButtonColor: '#e52e3d',
          cancelButtonColor: '#71717a'
        });

        if (destId) {
          self.showToast('info', 'Bulk SFTP transfer initiated...');
          const res = await self.apiCall('/jobs/bulk-ftp/dispatch', {
            method: 'POST',
            body: JSON.stringify({ jobIds: selectedJobIds, destinationId: parseInt(destId) })
          });
          self.showToast('success', res.message);
          await self.refreshBatchQAPage();
        }
      } catch (err) {
        self.showToast('error', err.message || 'Bulk SFTP transfer failed.');
      }
    });

    // Bulk Delete Click Handler
    $(document).off('click', '#btn-qa-bulk-delete').on('click', '#btn-qa-bulk-delete', async function() {
      const selectedJobIds = self.getSelectedJobIds();
      if (selectedJobIds.length === 0) return;

      const { isConfirmed } = await Swal.fire({
        title: `Delete ${selectedJobIds.length} Selected Batch Jobs?`,
        text: `Permanently delete ${selectedJobIds.length} batch job(s) and all their associated execution logs? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a',
        confirmButtonText: `Yes, Delete ${selectedJobIds.length} Jobs`
      });

      if (isConfirmed) {
        try {
          const res = await self.apiCall('/jobs/bulk-delete', {
            method: 'POST',
            body: JSON.stringify({ jobIds: selectedJobIds })
          });
          self.showToast('success', res.message || `${selectedJobIds.length} Batch Jobs deleted successfully.`);
          $('#qa-select-all').prop('checked', false);
          await self.refreshBatchQAPage();
        } catch (err) {
          self.showToast('error', err.message || 'Failed to delete selected jobs.');
        }
      }
    });

    // Batch Actions Click Handlers inside QA page
    $(document).off('click', '.btn-job-verify').on('click', '.btn-job-verify', function() {
      const jobId = $(this).attr('data-job-id');
      self.openJobVerificationModal(jobId);
    });

    $(document).off('click', '.btn-job-excel-type').on('click', '.btn-job-excel-type', async function(e) {
      e.preventDefault();
      if ($(this).hasClass('disabled')) return;
      const jobId = $(this).attr('data-job-id');
      const exportType = $(this).attr('data-type');
      const token = localStorage.getItem('edi_sig_token');
      
      try {
        self.showToast('info', `Preparing ${exportType} Excel export...`);
        const url = `${API_BASE}/jobs/${jobId}/export/excel?type=${exportType}&_t=${Date.now()}`;
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || `No ${exportType} records found for this job.`);
        }

        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `Job_${jobId.substring(0,8)}_${exportType}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        self.showToast('success', `Excel file (${exportType}) downloaded successfully.`);
      } catch (err) {
        self.showToast('error', err.message || 'Excel export failed.');
      }
    });

    $(document).off('click', '.btn-job-sftp').on('click', '.btn-job-sftp', async function(e) {
      if ($(this).is(':disabled') || $(this).hasClass('disabled')) {
        e.preventDefault();
        return;
      }
      const jobId = $(this).attr('data-job-id');
      const pendingCnt = parseInt($(this).attr('data-pending') || '0', 10);

      if (pendingCnt > 0) {
        self.showToast('warning', `Cannot dispatch SFTP: ${pendingCnt} ISIN(s) in this batch are still pending review. Please verify all ISINs first.`);
        return;
      }

      try {
        const dests = await self.apiCall('/ftp/destinations');
        if (dests.length === 0) {
          self.showToast('error', 'Please configure an SFTP destination in Settings first.');
          return;
        }
        const inputOptions = {};
        dests.forEach(d => { inputOptions[d.id] = `${d.name} (${d.host})`; });

        const { value: destId } = await Swal.fire({
          title: 'Dispatch Job Excel File',
          text: `Send Excel file for Job #${jobId.substring(0,8)} to target SFTP server:`,
          input: 'select',
          inputOptions,
          showCancelButton: true,
          confirmButtonColor: '#e52e3d',
          cancelButtonColor: '#71717a'
        });

        if (destId) {
          self.showToast('info', 'SFTP transfer initiated...');
          const res = await self.apiCall(`/jobs/${jobId}/ftp/dispatch`, {
            method: 'POST',
            body: JSON.stringify({ destinationId: parseInt(destId) })
          });
          self.showToast('success', res.message);
        }
      } catch (err) {
        self.showToast('error', err.message || 'SFTP transfer failed.');
      }
    });

    $(document).off('click', '.btn-delete-job').on('click', '.btn-delete-job', async function() {
      const jobId = $(this).attr('data-job-id');
      const { isConfirmed } = await Swal.fire({
        title: 'Delete Batch Job?',
        text: `Permanently delete Batch Job #${jobId.substring(0,8)} and its execution logs? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a',
        confirmButtonText: 'Yes, Delete Job'
      });

      if (isConfirmed) {
        try {
          const res = await self.apiCall(`/jobs/${jobId}`, { method: 'DELETE' });
          self.showToast('success', res.message || 'Batch Job deleted successfully.');
          await self.refreshBatchQAPage();
        } catch (err) {
          self.showToast('error', err.message || 'Failed to delete job.');
        }
      }
    });

    await this.refreshBatchQAPage();
  },

  getSelectedJobIds() {
    const selectedJobIds = [];
    $('.qa-job-checkbox:checked').each(function() {
      selectedJobIds.push($(this).attr('data-job-id'));
    });
    return selectedJobIds;
  },

  updateBulkDeleteVisibility() {
    const checkedCount = $('.qa-job-checkbox:checked').length;
    $('.qa-selected-count').text(checkedCount);
    if (checkedCount > 0) {
      $('#qa-bulk-actions-bar').removeClass('d-none').addClass('d-flex');
    } else {
      $('#qa-bulk-actions-bar').addClass('d-none').removeClass('d-flex');
    }
  },

  async refreshBatchQAPage() {
    const self = this;
    const page = this.qaCurrentPage || 1;
    const limit = this.qaLimit || 10;
    const search = this.qaSearch || '';

    try {
      // Fetch aggregate KPI stats for summary cards
      const statsRes = await this.apiCall('/jobs/stats').catch(() => null);
      if (statsRes) {
        $('#bqa-stat-total').text(statsRes.total_batches || 0);
        $('#bqa-stat-running').text(statsRes.running || 0);
        $('#bqa-stat-pending').text(statsRes.pending_review || 0);
        $('#bqa-stat-verified').text(statsRes.fully_verified || 0);
        $('#bqa-stat-dispatched').text(statsRes.completed || 0);
      }

      // Update Sort Arrow Icons
      $('.sort-icon').html('↕').removeClass('text-info text-white fw-bold').addClass('text-muted');
      const activeSortIcon = $(`#sort-icon-${this.qaSortBy}`);
      if (activeSortIcon.length) {
        activeSortIcon.html(this.qaSortOrder === 'asc' ? '▲' : '▼').removeClass('text-muted').addClass('text-info fw-bold');
      }

      const queryStr = `?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&status=${encodeURIComponent(this.qaStatus || 'all')}&sortBy=${encodeURIComponent(this.qaSortBy || 'created_at')}&sortOrder=${encodeURIComponent(this.qaSortOrder || 'desc')}`;
      const res = await this.apiCall(`/jobs${queryStr}`);
      
      const jobs = res.jobs || [];
      const total = res.total || 0;
      const totalPages = res.totalPages || 1;

      const tbody = $('#batch-qa-body');
      $('#qa-select-all').prop('checked', false);
      this.updateBulkDeleteVisibility();
      
      if (jobs.length === 0) {
        tbody.html('<tr><td colspan="9" class="text-center text-muted py-4">No matching batch ingestion jobs found.</td></tr>');
        $('#qa-pagination-info').text('Showing 0 to 0 of 0 batch jobs');
        $('#qa-pagination-controls').empty();
        return;
      }

      tbody.empty();
      jobs.forEach(j => {
        const pendingCnt = j.pending_review_count || 0;
        const validatedCnt = j.validated_count || 0;
        const rejectedCnt = j.rejected_count || 0;

        let qaProgressBadge = '';
        let statusBadge = '';
        let canDispatchSftp = false;

        if (j.status === 'running') {
          qaProgressBadge = '<span class="badge bg-warning text-dark"><span class="spinner-border spinner-border-sm me-1" role="status" style="width:10px;height:10px;"></span> Scraping Active</span>';
          statusBadge = '<span class="badge bg-warning text-dark">Running</span>';
        } else if (pendingCnt > 0) {
          qaProgressBadge = `<span class="badge bg-warning text-dark"><i data-feather="clock" style="width:12px;height:12px;" class="me-1"></i> ${pendingCnt} Pending Review</span>`;
          
          const ageHours = (Date.now() - new Date(j.created_at).getTime()) / (1000 * 60 * 60);
          if (ageHours >= 48) {
            statusBadge = '<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50 me-1">In Review</span><span class="badge bg-danger text-white border border-danger" title="Batch in review for ' + Math.floor(ageHours) + 'h (>48h). Requires immediate QA review."><i data-feather="alert-triangle" style="width:10px;height:10px;" class="me-1"></i> Stale — Needs Attention</span>';
          } else if (ageHours >= 24) {
            statusBadge = '<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50 me-1">In Review</span><span class="badge bg-warning text-dark border border-warning" title="Batch in review for ' + Math.floor(ageHours) + 'h (>24h)."><i data-feather="clock" style="width:10px;height:10px;" class="me-1"></i> Aging</span>';
          } else {
            statusBadge = '<span class="badge bg-info bg-opacity-25 text-info border border-info border-opacity-50">In Review</span>';
          }
        } else {
          qaProgressBadge = `<span class="badge bg-success"><i data-feather="check-circle" style="width:12px;height:12px;" class="me-1"></i> 100% Reviewed (${validatedCnt} V, ${rejectedCnt} R)</span>`;
          statusBadge = '<span class="badge bg-success">Fully Verified</span>';
          if (validatedCnt > 0) {
            canDispatchSftp = true;
          }
        }

        const exchangeBadge = this.getExchangeBadge(null, null, j.exchange_module);

        const sftpDisabledAttr = !canDispatchSftp ? 'disabled' : '';
        const sftpTitleAttr = !canDispatchSftp 
          ? `title="Cannot dispatch SFTP: ${pendingCnt > 0 ? `${pendingCnt} ISIN(s) pending review` : validatedCnt === 0 ? '0 validated ISIN records' : 'Scraper running'}"` 
          : 'title="Send validated records via SFTP"';

        const dispatchedBadge = j.last_dispatched_at
          ? `<span class="badge bg-secondary font-monospace tx-11 text-white border border-secondary" title="${new Date(j.last_dispatched_at).toLocaleString()}"><i data-feather="send" style="width:10px;height:10px;" class="me-1 text-info"></i>${new Date(j.last_dispatched_at).toLocaleString()}</span>`
          : `<span class="text-muted tx-12 font-monospace">Never</span>`;

        tbody.append(`
          <tr>
            <td style="width: 38px;">
              <input type="checkbox" class="form-check-input qa-job-checkbox cursor-pointer" data-job-id="${j.id}">
            </td>
            <td class="font-monospace fw-bold text-white"><span class="text-info">#${j.id.slice(0, 8)}</span></td>
            <td>${exchangeBadge}</td>
            <td class="fw-bold text-white">${j.total_isins}</td>
            <td>${qaProgressBadge}</td>
            <td>${statusBadge}</td>
            <td class="text-muted tx-12">${new Date(j.created_at).toLocaleString()}</td>
            <td>${dispatchedBadge}</td>
            <td class="text-end">
              <button class="btn btn-sm btn-outline-info btn-job-verify me-1 d-inline-flex align-items-center gap-1" data-job-id="${j.id}">
                <i data-feather="check-square" style="width: 14px; height: 14px;"></i> Open QA Workspace
              </button>
              
              <!-- 3-Way Excel Export Light Dropdown (dropup to prevent clipping) -->
              <div class="dropup d-inline-block me-1">
                <button class="btn btn-sm btn-outline-secondary dropdown-toggle d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="dropdown" data-bs-boundary="viewport" aria-expanded="false">
                  <i data-feather="download" style="width: 14px; height: 14px;"></i> Export Excel
                </button>
                <ul class="dropdown-menu dropdown-menu-end shadow border-0 rounded-3 py-1">
                  <li>
                    <a class="dropdown-item btn-job-excel-type text-dark d-flex align-items-center gap-2 tx-12 py-2" href="javascript:void(0);" data-job-id="${j.id}" data-type="full">
                      <i data-feather="file-text" style="width:14px;height:14px;" class="text-info"></i>
                      <span class="fw-semibold">Full Batch (Original Scraped)</span>
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item btn-job-excel-type text-dark d-flex align-items-center gap-2 tx-12 py-2 ${validatedCnt === 0 ? 'disabled text-muted opacity-50' : ''}" href="javascript:void(0);" data-job-id="${j.id}" data-type="validated">
                      <i data-feather="check-circle" style="width:14px;height:14px;" class="text-success"></i>
                      <span class="fw-semibold">Validated Records Only (${validatedCnt})</span>
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item btn-job-excel-type text-dark d-flex align-items-center gap-2 tx-12 py-2 ${rejectedCnt === 0 ? 'disabled text-muted opacity-50' : ''}" href="javascript:void(0);" data-job-id="${j.id}" data-type="rejected">
                      <i data-feather="x-circle" style="width:14px;height:14px;" class="text-danger"></i>
                      <span class="fw-semibold">Rejected Exceptions Only (${rejectedCnt})</span>
                    </a>
                  </li>
                </ul>
              </div>

              <button class="btn btn-sm btn-outline-warning btn-job-sftp me-1 d-inline-flex align-items-center gap-1" data-job-id="${j.id}" data-pending="${pendingCnt}" ${sftpDisabledAttr} ${sftpTitleAttr}>
                <i data-feather="send" style="width: 14px; height: 14px;"></i> Send Validated via SFTP
              </button>

              <button class="btn btn-sm btn-outline-danger btn-delete-job d-inline-flex align-items-center gap-1" data-job-id="${j.id}" title="Delete Batch Job">
                <i data-feather="trash-2" style="width: 14px; height: 14px;"></i>
              </button>
            </td>
          </tr>
        `);
      });

      // Update Pagination Summary & Controls
      const startIdx = (page - 1) * limit + 1;
      const endIdx = Math.min(page * limit, total);
      $('#qa-pagination-info').text(`Showing ${startIdx} to ${endIdx} of ${total} batch jobs`);

      const pagControls = $('#qa-pagination-controls');
      pagControls.empty();

      // Prev Button
      const prevDisabled = (page <= 1) ? 'disabled' : '';
      pagControls.append(`
        <li class="page-item ${prevDisabled}">
          <a class="page-link qa-page-link bg-dark text-white border-secondary" href="#" data-page="${page - 1}" aria-label="Previous">
            <span aria-hidden="true">&laquo;</span>
          </a>
        </li>
      `);

      // Smart Ellipsis Page Numbers Renderer (e.g. 1 ... 4 5 6 ... 20)
      const pagesToRender = [];
      const delta = 1;

      for (let p = 1; p <= totalPages; p++) {
        if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
          pagesToRender.push(p);
        } else if (pagesToRender[pagesToRender.length - 1] !== '...') {
          pagesToRender.push('...');
        }
      }

      pagesToRender.forEach(pItem => {
        if (pItem === '...') {
          pagControls.append(`
            <li class="page-item disabled">
              <span class="page-link bg-dark text-muted border-secondary px-2">...</span>
            </li>
          `);
        } else {
          const activeClass = (pItem === page) ? 'active bg-primary text-white border-primary' : 'bg-dark text-white border-secondary';
          pagControls.append(`
            <li class="page-item ${pItem === page ? 'active' : ''}">
              <a class="page-link qa-page-link ${activeClass}" href="#" data-page="${pItem}">${pItem}</a>
            </li>
          `);
        }
      });

      // Next Button
      const nextDisabled = (page >= totalPages) ? 'disabled' : '';
      pagControls.append(`
        <li class="page-item ${nextDisabled}">
          <a class="page-link qa-page-link bg-dark text-white border-secondary" href="#" data-page="${page + 1}" aria-label="Next">
            <span aria-hidden="true">&raquo;</span>
          </a>
        </li>
      `);

      if (typeof feather !== 'undefined') feather.replace();
      this.updateBulkDeleteVisibility();
    } catch (err) {
      console.warn('Batch QA refresh error:', err);
    }
  },

  // ---------------------------------------------------------------------------
  // Page: Job Monitoring Dashboard
  // ---------------------------------------------------------------------------
  async renderJobs(container) {
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0">Scraper Jobs Monitor</h4>
        </div>
      </div>

      <div class="row" id="active-job-card-section">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass border-warning">
            <div class="card-body">
              <h6 class="card-title text-warning d-flex align-items-center gap-2">
                <span class="spinner-border spinner-border-sm text-warning" role="status"></span>
                Active Headless Scraping Pool In Progress
              </h6>
              <div class="progress mt-3 mb-3" style="height: 15px;">
                <div id="job-progress-bar" class="progress-bar bg-warning progress-bar-striped progress-bar-animated" role="progressbar" style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100">0%</div>
              </div>
              <p class="text-muted tx-13">Headless workers are querying Börse Stuttgart detail records sequentially using custom anti-fingerprint delay configurations.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Scraper Ingestion Runs Historical Logs</h6>
              <div class="table-responsive">
                <table class="table table-hover align-middle" id="runs-history-table">
                  <thead>
                    <tr>
                      <th>Job UUID</th>
                      <th>Module</th>
                      <th>Status</th>
                      <th>ISINs</th>
                      <th>Processed</th>
                      <th>Failures</th>
                      <th>Initiated</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody id="runs-history-body">
                    <tr><td colspan="8" class="text-center text-muted">Querying database...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);

    await this.refreshJobsPage();
    
    // Set up real-time polling loop
    this.pollingInterval = setInterval(() => this.refreshJobsPage(), 2000);
  },

  async refreshJobsPage() {
    try {
      const res = await this.apiCall('/jobs');
      const jobs = Array.isArray(res) ? res : (res.jobs || []);
      const activeJob = jobs.find(j => j.status === 'running');
      
      if (activeJob) {
        $('#active-job-card-section').removeClass('d-none');
        this.activeJobId = activeJob.id;
        
        // Calculate progress percentage safely
        const pct = activeJob.total_isins > 0 ? Math.round((activeJob.processed / activeJob.total_isins) * 100) : 0;
        $('#job-progress-bar')
          .css('width', `${pct}%`)
          .attr('aria-valuenow', pct)
          .text(`${pct}% (${activeJob.processed}/${activeJob.total_isins})`);
      } else {
        $('#active-job-card-section').addClass('d-none');
        if (this.pollingInterval && this.activeJobId) {
          this.activeJobId = null;
          this.showToast('success', 'Scraper process finished.');
        }
      }

      // Populate history table
      const tbody = $('#runs-history-body');
      if (jobs.length === 0) {
        tbody.html('<tr><td colspan="8" class="text-center text-muted">No scraper execution runs found.</td></tr>');
        return;
      }

      tbody.empty();
      jobs.forEach(j => {
        let statusBadge = '';
        if (j.status === 'running') statusBadge = '<span class="badge bg-warning text-dark">Running</span>';
        else if (j.status === 'dispatched' || j.last_dispatched_at) statusBadge = '<span class="badge bg-primary">Dispatched</span>';
        else if (j.status === 'completed' || j.status === 'finished' || (j.processed > 0 && j.failed_count === 0)) statusBadge = '<span class="badge bg-success">Completed</span>';
        else if (j.status === 'cancelled') statusBadge = '<span class="badge bg-secondary">Cancelled</span>';
        else statusBadge = '<span class="badge bg-danger">Failed</span>';

        tbody.append(`
          <tr>
            <td class="font-monospace fw-bold"><a href="#/jobs?detail=${j.id}" class="text-info">${j.id.slice(0, 8)}...</a></td>
            <td><span class="text-white font-monospace">${j.exchange_module}</span></td>
            <td>${statusBadge}</td>
            <td class="text-white">${j.total_isins}</td>
            <td class="text-success">${j.processed}</td>
            <td class="text-danger">${j.failed_count}</td>
            <td class="text-muted tx-12">${new Date(j.created_at).toLocaleString()}</td>
            <td class="text-muted tx-12">${j.completed_at ? new Date(j.completed_at).toLocaleString() : '—'}</td>
          </tr>
        `);
      });

      if (typeof feather !== 'undefined') {
        feather.replace();
      }

    } catch (err) {
      console.warn('Jobs polling failure:', err);
    }
  },

  // Interactive Job Verification & Data Correction Modal
  openJobVerificationModal(jobId) {
    window.location.hash = `#/job-verify?jobId=${jobId}`;
  },

  // ---------------------------------------------------------------------------
  // Dedicated Page: Job Data Verification Console (Route: #/job-verify?jobId=...)
  // ---------------------------------------------------------------------------
  async renderJobVerificationPage(container) {
    const self = this;
    const jobId = this.queryParams.get('jobId');

    if (!jobId) {
      this.showToast('error', 'No Job ID provided for verification.');
      window.location.hash = '#/batch-qa';
      return;
    }

    // Initialize state properties for pagination & search
    this.jvCurrentPage = 1;
    this.jvLimit = 10;
    this.jvSearch = '';

    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin mb-4">
        <div>
          <div class="mb-2">
            <a href="#/batch-qa" class="text-muted tx-13 text-decoration-none d-inline-flex align-items-center gap-1">
              <i data-feather="arrow-left" style="width: 14px; height: 14px;"></i> Ingestion QA Queue / Job Verification
            </a>
          </div>
          <h4 class="mb-1 text-white fw-bold">Job Data Verification Console — <span class="text-info">Job #${jobId.substring(0,8)}</span></h4>
          <p class="text-muted tx-13 mb-0">
            Double-click any cell to edit inline.
          </p>
        </div>
        <div class="d-flex gap-2 align-items-center mt-2 mt-md-0">
          <button id="btn-page-approve-all" class="btn btn-sm btn-success d-inline-flex align-items-center gap-1">
            <i data-feather="check" style="width: 14px; height: 14px;"></i> Approve All ISINs
          </button>

          <!-- 3-Way Excel Export Light Dropdown (identical to Batch-QA) -->
          <div class="dropdown d-inline-block">
            <button class="btn btn-sm btn-outline-secondary dropdown-toggle d-inline-flex align-items-center gap-1" type="button" data-bs-toggle="dropdown" aria-expanded="false">
              <i data-feather="download" style="width: 14px; height: 14px;"></i> Export Excel
            </button>
            <ul class="dropdown-menu dropdown-menu-end shadow border-0 rounded-3 py-1">
              <li>
                <a class="dropdown-item btn-job-excel-type text-dark d-flex align-items-center gap-2 tx-12 py-2" href="javascript:void(0);" data-job-id="${jobId}" data-type="full">
                  <i data-feather="file-text" style="width:14px;height:14px;" class="text-info"></i>
                  <span class="fw-semibold">Full Batch (Original Scraped)</span>
                </a>
              </li>
              <li>
                <a class="dropdown-item btn-job-excel-type text-dark d-flex align-items-center gap-2 tx-12 py-2" href="javascript:void(0);" data-job-id="${jobId}" data-type="validated" id="excel-opt-validated">
                  <i data-feather="check-circle" style="width:14px;height:14px;" class="text-success"></i>
                  <span class="fw-semibold">Validated Records Only (<span id="jv-opt-validated-cnt">0</span>)</span>
                </a>
              </li>
              <li>
                <a class="dropdown-item btn-job-excel-type text-dark d-flex align-items-center gap-2 tx-12 py-2" href="javascript:void(0);" data-job-id="${jobId}" data-type="rejected" id="excel-opt-rejected">
                  <i data-feather="x-circle" style="width:14px;height:14px;" class="text-danger"></i>
                  <span class="fw-semibold">Rejected Exceptions Only (<span id="jv-opt-rejected-cnt">0</span>)</span>
                </a>
              </li>
            </ul>
          </div>

          <button class="btn btn-sm btn-outline-warning btn-job-sftp d-inline-flex align-items-center gap-1" id="btn-page-dispatch-sftp" data-job-id="${jobId}" data-pending="0">
            <i data-feather="send" style="width: 14px; height: 14px;"></i> Send Validated via SFTP
          </button>
        </div>
      </div>

      <!-- Summary Metrics Dashboard -->
      <div class="row mb-4">
        <div class="col-md-3 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-12 text-uppercase font-monospace mb-1">Total Batch ISINs</div>
                <h3 class="text-white fw-bold mb-0" id="card-count-total">—</h3>
              </div>
              <div class="wd-40 ht-40 rounded-circle bg-primary bg-opacity-25 d-flex align-items-center justify-content-center text-primary">
                <i data-feather="layers" class="wd-20 ht-20"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 grid-margin stretch-card">
          <div class="card card-glass border-start border-success border-3">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-12 text-uppercase font-monospace mb-1">Validated</div>
                <h3 class="text-success fw-bold mb-0" id="card-count-validated">—</h3>
              </div>
              <div class="wd-40 ht-40 rounded-circle bg-success bg-opacity-25 d-flex align-items-center justify-content-center text-success">
                <i data-feather="check-circle" class="wd-20 ht-20"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 grid-margin stretch-card">
          <div class="card card-glass border-start border-warning border-3">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-12 text-uppercase font-monospace mb-1">Pending Review</div>
                <h3 class="text-warning fw-bold mb-0" id="card-count-pending">—</h3>
              </div>
              <div class="wd-40 ht-40 rounded-circle bg-warning bg-opacity-25 d-flex align-items-center justify-content-center text-warning">
                <i data-feather="clock" class="wd-20 ht-20"></i>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-3 grid-margin stretch-card">
          <div class="card card-glass border-start border-danger border-3">
            <div class="card-body py-3 d-flex justify-content-between align-items-center">
              <div>
                <div class="text-muted tx-12 text-uppercase font-monospace mb-1">Rejected</div>
                <h3 class="text-danger fw-bold mb-0" id="card-count-rejected">—</h3>
              </div>
              <div class="wd-40 ht-40 rounded-circle bg-danger bg-opacity-25 d-flex align-items-center justify-content-center text-danger">
                <i data-feather="x-circle" class="wd-20 ht-20"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- QA Review Progress Bar -->
      <div class="row mb-4">
        <div class="col-md-12">
          <div class="card card-glass p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="tx-12 fw-bold text-white uppercase font-monospace">
                <i data-feather="check-square" class="wd-14 ht-14 me-1 text-success"></i> Batch QA Verification Progress:
                <span id="jv-qa-pct-text" class="text-success ms-1">0% Reviewed (0/0 ISINs)</span>
              </span>
              <span class="tx-11 text-muted font-monospace" id="jv-qa-remaining-text">0 ISINs pending review</span>
            </div>
            <div class="progress bg-dark border border-secondary" style="height: 8px;">
              <div id="jv-qa-progress-bar" class="progress-bar bg-success progress-bar-striped progress-bar-animated" role="progressbar" style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Reference Table Card -->
      <div class="row">
        <div class="col-md-12">
          <div class="card card-glass">
            <div class="card-body">
              
              <!-- Filter & Search Toolbar -->
              <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div class="d-flex align-items-center gap-2">
                  <label class="tx-12 text-muted mb-0">Show</label>
                  <select class="form-select form-select-sm bg-dark text-white border-secondary w-auto py-1" id="jv-page-limit">
                    <option value="10" selected>10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span class="tx-12 text-muted">per page</span>
                </div>

                <div class="input-group input-group-sm" style="width: 260px;">
                  <span class="input-group-text bg-dark border-secondary text-muted"><i data-feather="search" style="width:14px;height:14px;"></i></span>
                  <input type="text" class="form-control form-control-sm bg-dark text-white border-secondary" id="jv-search-input" placeholder="Search ISIN, WKN, Name or Issuer...">
                </div>
              </div>

              <!-- Table -->
              <div class="table-responsive">
                <table class="table table-bordered table-hover align-middle tx-13" id="page-verification-table">
                  <thead class="bg-dark text-white">
                    <tr>
                      <th style="width: 38px;"></th>
                      <th>ISIN</th>
                      <th>Exchange</th>
                      <th>Security Name / Issuer</th>
                      <th>WKN / Valor</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody id="jv-table-body">
                    <tr><td colspan="6" class="text-center text-muted py-4">Querying batch ISIN records...</td></tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination Footer Controls -->
              <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2 pt-2 border-top border-secondary border-opacity-25">
                <div class="tx-12 text-muted" id="jv-pagination-info">Showing 0 to 0 of 0 ISINs</div>
                <nav aria-label="Job ISIN Navigation">
                  <ul class="pagination pagination-sm mb-0 justify-content-end gap-1" id="jv-pagination-controls">
                  </ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    `);

    if (typeof feather !== 'undefined') feather.replace();

    // Event Handlers for Toolbar & Controls
    $('#jv-page-limit').off('change').on('change', function() {
      self.jvLimit = parseInt($(this).val(), 10);
      self.jvCurrentPage = 1;
      self.refreshJobVerificationTable(jobId);
    });

    let searchDebounce;
    $('#jv-search-input').off('keyup input').on('keyup input', function() {
      clearTimeout(searchDebounce);
      const val = $(this).val();
      searchDebounce = setTimeout(() => {
        self.jvSearch = val;
        self.jvCurrentPage = 1;
        self.refreshJobVerificationTable(jobId);
      }, 300);
    });

    $(document).off('click', '.jv-page-link').on('click', '.jv-page-link', function(e) {
      e.preventDefault();
      const targetPage = parseInt($(this).attr('data-page'), 10);
      if (targetPage && targetPage !== self.jvCurrentPage) {
        self.jvCurrentPage = targetPage;
        self.refreshJobVerificationTable(jobId);
      }
    });

    // Top Action: Approve all ISINs
    $('#btn-page-approve-all').off('click').on('click', async () => {
      try {
        await self.apiCall(`/data/bonds/validate-all?jobId=${jobId}`, { method: 'POST' });
        self.showToast('success', `All ISIN records in this batch approved.`);
        await self.refreshJobVerificationTable(jobId);
      } catch (err) {
        self.showToast('error', 'Batch approval error.');
      }
    });

    // Top Action: Dispatch File to SFTP
    $('#btn-page-dispatch-sftp').off('click').on('click', async () => {
      try {
        const stats = await self.apiCall(`/jobs/${jobId}/stats`).catch(() => ({}));
        const pendingCnt = stats.pending_count || 0;
        const validatedCnt = stats.validated_count || 0;

        if (pendingCnt > 0) {
          self.showToast('warning', `Cannot dispatch SFTP: ${pendingCnt} ISIN(s) in this batch are still pending review. Please approve or flag all records first.`);
          return;
        }

        if (validatedCnt === 0) {
          self.showToast('warning', `Cannot dispatch SFTP: 0 validated ISIN records found in this batch.`);
          return;
        }

        const destinations = await self.apiCall('/ftp/destinations').catch(() => []);
        if (!destinations || destinations.length === 0) {
          self.showToast('warning', 'Please configure an SFTP destination in Settings first.');
          return;
        }

        const inputOptions = {};
        destinations.forEach(d => { inputOptions[d.id] = `${d.name} (${d.host})`; });

        const { value: destId } = await Swal.fire({
          title: 'Dispatch Job Excel File',
          text: `Send ${validatedCnt} validated Excel record(s) for Job #${jobId.substring(0,8)} to target SFTP server:`,
          input: 'select',
          inputOptions,
          showCancelButton: true,
          confirmButtonColor: '#e52e3d',
          cancelButtonColor: '#71717a'
        });

        if (destId) {
          self.showToast('info', 'SFTP transfer initiated...');
          const res = await self.apiCall(`/jobs/${jobId}/ftp/dispatch`, {
            method: 'POST',
            body: JSON.stringify({ destinationId: parseInt(destId, 10) })
          });
          self.showToast('success', res.message || 'Excel file dispatched to SFTP server.');
        }
      } catch (err) {
        self.showToast('error', err.message || 'SFTP transfer failed.');
      }
    });

    this.setupJobVerificationKeyboardShortcuts();
    await this.refreshJobVerificationTable(jobId);
  },

  /**
   * Keyboard Shortcuts Handler for QA Operators in Job Verification Console
   */
  setupJobVerificationKeyboardShortcuts() {
    const self = this;

    $(document).off('keydown.jvKeyboard').on('keydown.jvKeyboard', function(e) {
      // Only execute if on #/job-verify route
      if (!window.location.hash.startsWith('#/job-verify')) return;

      // Ignore if user is actively typing in an editable field or modal
      const activeTag = document.activeElement ? document.activeElement.tagName.toLowerCase() : '';
      const isEditable = document.activeElement && (
        document.activeElement.isContentEditable ||
        activeTag === 'input' ||
        activeTag === 'textarea' ||
        activeTag === 'select'
      );
      if (isEditable) return;

      const rows = $('#jv-table-body tr[data-bond-id]');
      if (rows.length === 0) return;

      let focusedRow = $('#jv-table-body tr.table-row-focused');
      if (focusedRow.length === 0) {
        focusedRow = rows.first().addClass('table-row-focused');
      }

      const key = e.key;

      if (key === 'ArrowDown') {
        e.preventDefault();
        const next = focusedRow.nextAll('tr[data-bond-id]').first();
        if (next.length) {
          focusedRow.removeClass('table-row-focused');
          next.addClass('table-row-focused');
          next[0].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (key === 'ArrowUp') {
        e.preventDefault();
        const prev = focusedRow.prevAll('tr[data-bond-id]').first();
        if (prev.length) {
          focusedRow.removeClass('table-row-focused');
          prev.addClass('table-row-focused');
          prev[0].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      } else if (key === 'Enter') {
        e.preventDefault();
        const bondId = focusedRow.attr('data-bond-id');
        if (bondId) {
          $(`.toggle-expand-modal-row[data-bond-id="${bondId}"]`).trigger('click');
        }
      } else if (key === 'Escape' || key === 'Esc') {
        e.preventDefault();
        $('.modal-detail-row').addClass('d-none');
        $('.toggle-expand-modal-row svg').removeClass('rotate-90');
      } else if (key === 'a' || key === 'A') {
        e.preventDefault();
        const bondId = focusedRow.attr('data-bond-id');
        if (bondId) {
          $(`.btn-modal-validate[data-bond-id="${bondId}"]`).trigger('click');
        }
      } else if (key === 'r' || key === 'R') {
        e.preventDefault();
        const bondId = focusedRow.attr('data-bond-id');
        if (bondId) {
          // Expand detail row if closed
          $(`#detail-modal-${bondId}`).removeClass('d-none');
          $(`.toggle-expand-modal-row[data-bond-id="${bondId}"] svg`).addClass('rotate-90');
          // Open rejection box and focus reason input
          const rejectBtn = $(`.btn-toggle-modal-reject[data-bond-id="${bondId}"]`);
          if (rejectBtn.length) {
            rejectBtn.trigger('click');
            setTimeout(() => $(`#reject-reason-${bondId}`).focus(), 150);
          }
        }
      }
    });
  },

  async refreshJobVerificationTable(jobId) {
    const self = this;
    const page = this.jvCurrentPage || 1;
    const limit = this.jvLimit || 10;
    const search = this.jvSearch || '';

    // Capture currently expanded modal detail rows before refresh
    const expandedRowIds = $('.modal-detail-row:not(.d-none)').map(function() {
      return $(this).attr('id');
    }).get();

    try {
      const res = await this.apiCall(`/jobs/${jobId}/bonds?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
      
      const bonds = res.bonds || (Array.isArray(res) ? res : []);
      const total = res.total !== undefined ? res.total : bonds.length;
      const totalPages = res.totalPages || 1;
      const stats = res.stats || {
        total,
        validated: bonds.filter(b => b.validation_status === 'validated' || b.validation_status === 'approved').length,
        rejected: bonds.filter(b => b.validation_status === 'rejected' || b.validation_status === 'flagged').length,
        pending: bonds.filter(b => b.validation_status !== 'validated' && b.validation_status !== 'approved' && b.validation_status !== 'rejected' && b.validation_status !== 'flagged').length
      };

      // Update Metric Cards
      $('#card-count-total').text(stats.total);
      $('#card-count-validated').text(stats.validated);
      $('#card-count-pending').text(stats.pending);
      $('#card-count-rejected').text(stats.rejected);

      // Sync Export Excel dropdown counts & SFTP pending count (matching Batch-QA)
      $('#btn-page-dispatch-sftp').attr('data-pending', stats.pending);
      $('#jv-opt-validated-cnt').text(stats.validated);
      $('#jv-opt-rejected-cnt').text(stats.rejected);

      // Enable 'Send Validated via SFTP' ONLY if all batch ISINs are verified (pending === 0) AND validated > 0
      if (stats.pending > 0 || stats.validated === 0) {
        const reason = stats.pending > 0 
          ? `Cannot dispatch SFTP: ${stats.pending} ISIN(s) pending review` 
          : 'Cannot dispatch SFTP: 0 validated ISIN records';
        $('#btn-page-dispatch-sftp')
          .prop('disabled', true)
          .attr('title', reason);
      } else {
        $('#btn-page-dispatch-sftp')
          .prop('disabled', false)
          .attr('title', 'Send validated records via SFTP');
      }

      if (stats.validated === 0) {
        $('#excel-opt-validated').addClass('disabled text-muted opacity-50');
      } else {
        $('#excel-opt-validated').removeClass('disabled text-muted opacity-50');
      }

      if (stats.rejected === 0) {
        $('#excel-opt-rejected').addClass('disabled text-muted opacity-50');
      } else {
        $('#excel-opt-rejected').removeClass('disabled text-muted opacity-50');
      }

      // Update Thin Progress Bar
      const reviewedCount = (stats.validated || 0) + (stats.rejected || 0);
      const totalCount = stats.total || 1;
      const pct = Math.round((reviewedCount / totalCount) * 100);

      $('#jv-qa-pct-text').text(`${pct}% Reviewed (${reviewedCount}/${stats.total} ISINs)`);
      $('#jv-qa-remaining-text').text(`${stats.pending} ISINs pending review`);
      $('#jv-qa-progress-bar')
        .css('width', `${pct}%`)
        .attr('aria-valuenow', pct);

      const tbody = $('#jv-table-body');
      if (bonds.length === 0) {
        tbody.html('<tr><td colspan="9" class="text-center text-muted py-4">No ISIN reference records found matching criteria.</td></tr>');
        $('#jv-pagination-info').text('Showing 0 to 0 of 0 ISINs');
        $('#jv-pagination-controls').empty();
        return;
      }

      const rowsHtml = bonds.map((b) => {
        let statusBadge = '';
        if (b.validation_status === 'validated' || b.validation_status === 'approved') {
          statusBadge = '<span class="badge bg-success">Validated</span>';
        } else if (b.validation_status === 'rejected' || b.validation_status === 'flagged') {
          statusBadge = `<span class="badge bg-danger" title="${sanitize(b.validation_note || '')}">${b.validation_status === 'flagged' ? 'Flagged Anomaly' : 'Rejected'}</span>`;
        } else {
          statusBadge = '<span class="badge bg-warning text-dark">Pending Review</span>';
        }

        const exchangeBadge = self.getExchangeBadge(b.mic_code, b.isin, b.exchange_module);

        let rejectionBanner = '';
        if (b.validation_status === 'rejected') {
          rejectionBanner = `
            <div class="alert bg-danger-light text-danger border border-danger mb-3 py-2 px-3 rounded d-flex align-items-center gap-2 tx-13">
              <i data-feather="alert-octagon" style="width: 18px; height: 18px; flex-shrink: 0;"></i>
              <div>
                <strong>Record Rejected:</strong> "${sanitize(b.validation_note || 'No reason specified')}"
              </div>
            </div>
          `;
        }

        const safeIsin = sanitize(b.isin);
        const safeDesc1 = sanitize(b.security_desc_1);
        const safeDesc2 = sanitize(b.security_desc_2);
        const safeIssuer = sanitize(b.issuer_name);
        const safeWkn = sanitize((b.wkn && !b.wkn.startsWith('NF')) ? b.wkn : (b.local_code || '—'));
        const safeLocalCode = sanitize(b.local_code);
        const safeMic = sanitize(b.mic_code);

        const mainRow = `
          <tr data-bond-id="${b.id}" class="${b.availability === 'NO' ? 'row-no-availability' : ''}">
            <td>
              <button class="btn btn-link btn-xs p-0 text-muted toggle-expand-modal-row" data-bond-id="${b.id}">
                <i data-feather="chevron-right" class="wd-18 ht-18"></i>
              </button>
            </td>
            <td class="font-monospace fw-bold text-white">${safeIsin}</td>
            <td>${exchangeBadge}</td>
            <td>
              <div class="fw-bold text-white">${safeDesc1 || safeIssuer || 'Bond Record'}</div>
              <div class="tx-11 text-muted">${safeIssuer || '—'}</div>
            </td>
            <td class="font-monospace text-white">${safeWkn}</td>
            <td id="status-badge-${b.id}">${statusBadge}</td>
          </tr>
        `;

        const detailRow = self.renderBondDetailPanel(b, {
          context: 'job_verify',
          jobId,
          isAdmin: self.currentUser && self.currentUser.role_name === 'admin'
        });

        return mainRow + detailRow;
      }).join('');

      tbody.html(rowsHtml);

      // Pagination Summary & Controls
      const startIdx = (page - 1) * limit + 1;
      const endIdx = Math.min(page * limit, total);
      $('#jv-pagination-info').text(`Showing ${startIdx} to ${endIdx} of ${total} ISINs`);

      const pagControls = $('#jv-pagination-controls');
      pagControls.empty();

      const prevDisabled = (page <= 1) ? 'disabled' : '';
      pagControls.append(`
        <li class="page-item ${prevDisabled}">
          <a class="page-link jv-page-link bg-dark text-white border-secondary" href="#" data-page="${page - 1}" aria-label="Previous">
            <span aria-hidden="true">&laquo;</span>
          </a>
        </li>
      `);

      const pagesToRender = [];
      const delta = 1;

      for (let p = 1; p <= totalPages; p++) {
        if (p === 1 || p === totalPages || (p >= page - delta && p <= page + delta)) {
          pagesToRender.push(p);
        } else if (pagesToRender[pagesToRender.length - 1] !== '...') {
          pagesToRender.push('...');
        }
      }

      pagesToRender.forEach(pItem => {
        if (pItem === '...') {
          pagControls.append(`
            <li class="page-item disabled">
              <span class="page-link bg-dark text-muted border-secondary px-2">...</span>
            </li>
          `);
        } else {
          const activeClass = (pItem === page) ? 'active bg-primary text-white border-primary' : 'bg-dark text-white border-secondary';
          pagControls.append(`
            <li class="page-item ${pItem === page ? 'active' : ''}">
              <a class="page-link jv-page-link ${activeClass}" href="#" data-page="${pItem}">${pItem}</a>
            </li>
          `);
        }
      });

      const nextDisabled = (page >= totalPages) ? 'disabled' : '';
      pagControls.append(`
        <li class="page-item ${nextDisabled}">
          <a class="page-link jv-page-link bg-dark text-white border-secondary" href="#" data-page="${page + 1}" aria-label="Next">
            <span aria-hidden="true">&raquo;</span>
          </a>
        </li>
      `);

      if (typeof feather !== 'undefined') feather.replace();
      this.registerJobVerificationRowHandlers(jobId);

      // Restore previously expanded modal detail rows
      if (expandedRowIds && expandedRowIds.length > 0) {
        expandedRowIds.forEach(id => {
          if (id) {
            $(`#${id}`).removeClass('d-none');
            const bondId = id.replace('detail-modal-', '');
            $(`.toggle-expand-modal-row[data-bond-id="${bondId}"] svg`).addClass('rotate-90');
          }
        });
      }

    } catch (err) {
      console.error('Failed to refresh job verification table:', err);
      self.showToast('error', 'Error loading batch records: ' + err.message);
    }
  },

  registerJobVerificationRowHandlers(jobId) {
    const self = this;

    // Inline editor on page
    self.registerInlineEditor(() => self.refreshJobVerificationTable(jobId));

    // Expand row toggle
    $('.toggle-expand-modal-row').off('click').on('click', function() {
      const id = $(this).attr('data-bond-id');
      const isCollapsed = $(`#detail-modal-${id}`).hasClass('d-none');
      $('.modal-detail-row').addClass('d-none');
      $('.toggle-expand-modal-row svg').removeClass('rotate-90');

      if (isCollapsed) {
        $(`#detail-modal-${id}`).removeClass('d-none');
        $(this).find('svg').addClass('rotate-90');
      }
    });

    // Individual row approve
    $('.btn-modal-validate').off('click').on('click', async function() {
      const bondId = $(this).attr('data-bond-id');
      try {
        await self.apiCall(`/data/bonds/${bondId}/validate?jobId=${jobId}`, { method: 'POST' });
        self.showToast('success', 'ISIN record verified & approved.');
        await self.refreshJobVerificationTable(jobId);
      } catch (err) {
        self.showToast('error', err.message);
      }
    });

    // Toggle inline rejection box
    $('.btn-toggle-modal-reject').off('click').on('click', function() {
      const bondId = $(this).attr('data-bond-id');
      $(`#reject-box-${bondId}`).toggleClass('d-none');
      $(`#delete-box-${bondId}`).addClass('d-none');
    });

    $('.btn-cancel-modal-reject').off('click').on('click', function() {
      const bondId = $(this).attr('data-bond-id');
      $(`#reject-box-${bondId}`).addClass('d-none');
    });

    // Submit inline rejection
    $('.btn-submit-modal-reject').off('click').on('click', async function() {
      const bondId = $(this).attr('data-bond-id');
      const isin = $(this).attr('data-isin');
      const targetKey = (bondId && bondId !== 'null' && bondId !== 'undefined') ? bondId : isin;
      const reason = $(`#reject-reason-${targetKey}`).val().trim();
      if (!reason) {
        self.showToast('warning', 'Please enter a rejection reason.');
        return;
      }
      try {
        await self.apiCall(`/data/bonds/${targetKey}/reject?jobId=${jobId}`, {
          method: 'POST',
          body: JSON.stringify({ note: reason, reason, jobId })
        });
        self.showToast('warning', 'Record rejected.');
        await self.refreshJobVerificationTable(jobId);
      } catch (err) {
        self.showToast('error', err.message);
      }
    });

    // Toggle inline delete box
    $('.btn-toggle-modal-delete').off('click').on('click', function() {
      const bondId = $(this).attr('data-bond-id');
      const isin = $(this).attr('data-isin');
      const targetKey = (bondId && bondId !== 'null' && bondId !== 'undefined') ? bondId : isin;
      $(`#delete-box-${targetKey}`).toggleClass('d-none');
      $(`#reject-box-${targetKey}`).addClass('d-none');
    });

    $('.btn-cancel-modal-delete').off('click').on('click', function() {
      const bondId = $(this).attr('data-bond-id');
      const isin = $(this).attr('data-isin');
      const targetKey = (bondId && bondId !== 'null' && bondId !== 'undefined') ? bondId : isin;
      $(`#delete-box-${targetKey}`).addClass('d-none');
    });

    // Submit inline deletion
    $('.btn-submit-modal-delete').off('click').on('click', async function() {
      const bondId = $(this).attr('data-bond-id');
      const isin = $(this).attr('data-isin');
      const deleteTarget = (bondId && bondId !== 'null' && bondId !== 'undefined') ? bondId : isin;

      try {
        await self.apiCall(`/data/bonds/${deleteTarget}`, { method: 'DELETE' });
        self.showToast('success', 'Record permanently deleted.');
        await self.refreshJobVerificationTable(jobId);
      } catch (err) {
        self.showToast('error', err.message);
      }
    });

    // View Edit History in modal
    $('.btn-modal-view-history').off('click').on('click', async function() {
      const bondId = $(this).attr('data-bond-id');
      const historyContainer = $(`#history-modal-${bondId}`);
      
      if (!historyContainer.hasClass('d-none')) {
        historyContainer.addClass('d-none');
        return;
      }

      try {
        const logs = await self.apiCall(`/data/bonds/${bondId}/history`);
        if (logs.length === 0) {
          historyContainer.html('<span class="text-muted">No audit history found for this record.</span>').removeClass('d-none');
          return;
        }

        let html = '<div class="fw-bold mb-1 text-white">ISO 27001 Audit Log:</div>';
        logs.forEach(l => {
          html += `<div class="tx-11 mb-1">• ${new Date(l.changed_at).toLocaleString()} | Field: <strong class="text-info">${sanitize(l.field_name)}</strong> | ${sanitize(l.old_value || 'NULL')} ➔ <strong class="text-success">${sanitize(l.new_value)}</strong> by ${sanitize(l.changed_by || 'system')}</div>`;
        });

        historyContainer.html(html).removeClass('d-none');
      } catch (err) {
        historyContainer.html(`<span class="text-danger">Failed to load history: ${err.message}</span>`).removeClass('d-none');
      }
    });
  },

  // ---------------------------------------------------------------------------
  // Page: Data Explorer (Inline Editor, Verification, Validate, Export, SFTP)
  // ---------------------------------------------------------------------------
  async renderDataExplorer(container) {
    const self = this;
    const initialStatus = this.queryParams.get('status') || 'pending';
    
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0">Ingested Bond Records Explorer</h4>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button class="btn btn-success btn-icon-text" id="btn-bulk-export">
            <i class="btn-icon-prepend" data-feather="download"></i> Export Validated to Excel
          </button>
          <button class="btn btn-danger btn-icon-text" id="btn-bulk-ftp">
            <i class="btn-icon-prepend" data-feather="navigation"></i> Send Validated via FTP
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              
              <!-- Auto-Apply Scalable Filter Bar -->
              <div class="row mb-4 align-items-center g-2">
                <div class="col-md-3">
                  <label class="form-label text-muted tx-12 mb-1">Validation Status</label>
                  <select class="form-select form-select-sm bg-dark border-secondary text-white explorer-auto-filter" id="filter-status">
                    <option value="all" ${initialStatus === 'all' ? 'selected' : ''}>All Records</option>
                    <option value="pending" ${initialStatus === 'pending' ? 'selected' : ''}>Pending Review</option>
                    <option value="validated" ${initialStatus === 'validated' ? 'selected' : ''}>Validated</option>
                    <option value="rejected" ${initialStatus === 'rejected' ? 'selected' : ''}>Rejected</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label text-muted tx-12 mb-1">Exchange Source (100+ Platforms)</label>
                  <select class="form-select form-select-sm bg-dark border-secondary text-white explorer-auto-filter" id="filter-exchange">
                    <option value="all">All Exchanges & MICs</option>
                    <option value="XSWX">🇨🇭 SIX Swiss Exchange (XSWX / CH)</option>
                    <option value="XSTU">🇩🇪 Börse Stuttgart (XSTU / DE)</option>
                    <option value="XFRA">🇩🇪 Frankfurt Stock Exchange (XFRA)</option>
                    <option value="XPAR">🇫🇷 Euronext Paris (XPAR)</option>
                    <option value="XPTY">🇵🇦 Latinex — Bolsa Latinoamericana (XPTY)</option>
                    <option value="BVPASA">🇵🇾 Asunción Stock Exchange (BVPASA / XASU)</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label text-muted tx-12 mb-1">Currency</label>
                  <select class="form-select form-select-sm bg-dark border-secondary text-white explorer-auto-filter" id="filter-currency">
                    <option value="all">All Currencies</option>
                    <option value="CHF">CHF (Swiss Franc)</option>
                    <option value="EUR">EUR (Euro)</option>
                    <option value="USD">USD (US Dollar)</option>
                    <option value="PYG">PYG (Paraguayan Guaraní)</option>
                    <option value="PAB">PAB (Panamanian Balboa)</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label class="form-label text-muted tx-12 mb-1">Search String</label>
                  <div class="input-group input-group-sm">
                    <span class="input-group-text bg-dark border-secondary text-muted"><i data-feather="search" style="width:14px;height:14px;"></i></span>
                    <input type="text" class="form-control form-control-sm bg-dark border-secondary text-white" id="search-input-box" placeholder="Search ISIN, WKN, Name or Issuer...">
                  </div>
                </div>
              </div>

              <!-- Data Explorer Table -->
              <div class="table-responsive">
                <table class="table table-bordered table-hover align-middle" id="bonds-explorer-table">
                  <thead>
                    <tr>
                      <th style="width: 40px;"></th>
                      <th>ISIN</th>
                      <th>Exchange Source</th>
                      <th>Security Description / Issuer</th>
                      <th class="d-none d-xl-table-cell">WKN / Valor</th>
                      <th class="text-end">Coupon</th>
                      <th>Maturity</th>
                      <th>Currency</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody id="bonds-explorer-body">
                    <tr><td colspan="9" class="text-center text-muted">Querying database...</td></tr>
                  </tbody>
                </table>
              </div>

              <!-- Pagination Info -->
              <div class="d-flex justify-content-between align-items-center mt-3">
                <div class="text-muted tx-13">Showing <span id="pagination-start">0</span> to <span id="pagination-end">0</span> of <span id="pagination-total">0</span> entries</div>
                <nav>
                  <ul class="pagination pagination-sm mb-0" id="pagination-nav"></ul>
                </nav>
              </div>

            </div>
          </div>
        </div>
      </div>
    `);

    // Load available dynamic exchange MIC codes from API
    try {
      const activeMics = await this.apiCall('/exchanges');
      if (Array.isArray(activeMics)) {
        activeMics.forEach(mic => {
          if (!['XSWX', 'XSTU', 'XFRA', 'XPAR', 'XPTY', 'BVPASA'].includes(mic)) {
            $('#filter-exchange').append(`<option value="${mic}">${mic} Exchange</option>`);
          }
        });
      }
    } catch (e) {}

    // Fetch and render initial explorer table data
    let currentLimit = 15;
    let currentOffset = 0;

    const loadExplorerData = async () => {
      const status = $('#filter-status').val();
      const exchange = $('#filter-exchange').val();
      const currency = $('#filter-currency').val();
      const search = $('#search-input-box').val();
      
      try {
        const res = await this.apiCall(`/data/bonds?limit=${currentLimit}&offset=${currentOffset}&status=${status}&exchange=${exchange}&currency=${currency}&search=${encodeURIComponent(search)}`);
        
        const tbody = $('#bonds-explorer-body').empty();
        $('#pagination-total').text(res.total);
        
        if (res.total === 0) {
          tbody.html('<tr><td colspan="9" class="text-center text-muted py-4">No reference data records match the current filters.</td></tr>');
          $('#pagination-start').text(0);
          $('#pagination-end').text(0);
          $('#pagination-nav').empty();
          return;
        }

        $('#pagination-start').text(currentOffset + 1);
        $('#pagination-end').text(Math.min(currentOffset + currentLimit, res.total));

        res.data.forEach(b => {
          let statusBadge = '';
          if (b.validation_status === 'validated') {
            statusBadge = '<span class="badge bg-success">Validated</span>';
          } else if (b.validation_status === 'rejected' || b.validation_status === 'flagged') {
            statusBadge = `<span class="badge bg-danger" title="${sanitize(b.validation_note || '')}">${b.validation_status === 'flagged' ? 'Flagged Anomaly' : 'Rejected'}</span>`;
          } else {
            statusBadge = '<span class="badge bg-warning text-dark">Pending Review</span>';
          }

          // Exchange Source Badge
          const exchangeBadge = this.getExchangeBadge(b.mic_code, b.isin, b.exchange_module);

          let rejectionBanner = '';
          if (b.validation_status === 'rejected') {
            rejectionBanner = `
              <div class="alert bg-danger-light text-danger border border-danger mb-3 py-2 px-3 rounded d-flex align-items-center gap-2 tx-13">
                <i data-feather="alert-octagon" style="width: 18px; height: 18px; flex-shrink: 0;"></i>
                <div>
                  <strong>Record Rejected:</strong> "${sanitize(b.validation_note || 'No reason specified')}"
                </div>
              </div>
            `;
          }

          const safeIsin = sanitize(b.isin);
          const safeDesc1 = sanitize(b.security_desc_1);
          const safeDesc2 = sanitize(b.security_desc_2);
          const safeIssuer = sanitize(b.issuer_name);
          const safeWkn = sanitize((b.wkn && !b.wkn.startsWith('NF')) ? b.wkn : (b.local_code || '—'));
          const safeLocalCode = sanitize(b.local_code);
          const safeMic = sanitize(b.mic_code);

          const mainRow = `
            <tr data-bond-id="${b.id}" class="${b.availability === 'NO' ? 'row-no-availability' : ''}">
              <td>
                <button class="btn btn-link btn-xs p-0 text-muted toggle-expand-row" data-bond-id="${b.id}">
                  <i data-feather="chevron-right" class="wd-18 ht-18"></i>
                </button>
              </td>
              <td class="font-monospace fw-bold text-white">${safeIsin}</td>
              <td>${exchangeBadge}</td>
              <td>
                <div class="fw-bold text-white">${safeDesc1 || safeIssuer || 'Bond Record'}</div>
                <div class="tx-11 text-muted">${safeIssuer || '—'}</div>
              </td>
              <td class="font-monospace text-white d-none d-xl-table-cell">${safeWkn}</td>
              <td class="text-end fw-bold text-white">${b.interest_rate ? parseFloat(b.interest_rate) + '%' : '—'}</td>
              <td>${b.maturity_date ? sanitize(b.maturity_date.split('T')[0]) : '—'}</td>
              <td><span class="badge bg-secondary font-monospace">${sanitize(b.debt_currency || b.issue_currency || 'CHF')}</span></td>
              <td>${statusBadge}</td>
            </tr>
          `;

          const detailRow = self.renderBondDetailPanel(b, {
            context: 'explorer',
            isAdmin: self.currentUser && self.currentUser.role_name === 'admin'
          });

          tbody.append(mainRow + detailRow);
        });

        // Initialize Feather icons after render
        if (typeof feather !== 'undefined') {
          feather.replace();
        }

        // Expanded Row Toggle (Accordion style: only one open at a time)
        $('.toggle-expand-row').off('click').on('click', function() {
          const id = $(this).attr('data-bond-id');
          const isCollapsed = $(`#detail-${id}`).hasClass('d-none');
          
          // Collapse all others
          $('.detail-row').addClass('d-none');
          $('.toggle-expand-row svg').removeClass('rotate-90');
          const wasTracked = self.expandedBondIds.has(id);
          self.expandedBondIds.clear();

          if (isCollapsed) {
            $(`#detail-${id}`).removeClass('d-none');
            $(this).find('svg').addClass('rotate-90');
            self.expandedBondIds.add(id);
          }
        });

        // Restore expanded state if any row was previously expanded
        self.expandedBondIds.forEach(id => {
          $(`#detail-${id}`).removeClass('d-none');
          $(`.toggle-expand-row[data-bond-id="${id}"] svg`).addClass('rotate-90');
        });

        // Smart Ellipsis Pagination & Prev/Next Controls Rendering
        const totalPages = Math.ceil(res.total / currentLimit) || 1;
        const currentPage = Math.floor(currentOffset / currentLimit) + 1;
        const nav = $('#pagination-nav').empty();

        // Prev Button
        const prevDisabled = (currentPage <= 1) ? 'disabled' : '';
        const prevOffset = Math.max(0, (currentPage - 2) * currentLimit);
        nav.append(`
          <li class="page-item ${prevDisabled}">
            <a class="page-link explorer-page-link bg-dark text-white border-secondary" href="javascript:;" data-offset="${prevOffset}" aria-label="Previous">
              <span aria-hidden="true">&laquo;</span>
            </a>
          </li>
        `);

        // Smart Ellipsis Page Numbers
        const pagesToRender = [];
        const delta = 1;

        for (let p = 1; p <= totalPages; p++) {
          if (p === 1 || p === totalPages || (p >= currentPage - delta && p <= currentPage + delta)) {
            pagesToRender.push(p);
          } else if (pagesToRender[pagesToRender.length - 1] !== '...') {
            pagesToRender.push('...');
          }
        }

        pagesToRender.forEach(pItem => {
          if (pItem === '...') {
            nav.append(`
              <li class="page-item disabled">
                <span class="page-link bg-dark text-muted border-secondary px-2">...</span>
              </li>
            `);
          } else {
            const pageOffset = (pItem - 1) * currentLimit;
            const activeClass = (pItem === currentPage) ? 'active bg-primary text-white border-primary' : 'bg-dark text-white border-secondary';
            nav.append(`
              <li class="page-item ${pItem === currentPage ? 'active' : ''}">
                <a class="page-link explorer-page-link ${activeClass}" href="javascript:;" data-offset="${pageOffset}">${pItem}</a>
              </li>
            `);
          }
        });

        // Next Button
        const nextDisabled = (currentPage >= totalPages) ? 'disabled' : '';
        const nextOffset = Math.min((totalPages - 1) * currentLimit, currentPage * currentLimit);
        nav.append(`
          <li class="page-item ${nextDisabled}">
            <a class="page-link explorer-page-link bg-dark text-white border-secondary" href="javascript:;" data-offset="${nextOffset}" aria-label="Next">
              <span aria-hidden="true">&raquo;</span>
            </a>
          </li>
        `);

        nav.find('.explorer-page-link').off('click').on('click', function(e) {
          e.preventDefault();
          if ($(this).parent().hasClass('disabled')) return;
          const newOffset = parseInt($(this).attr('data-offset'), 10);
          if (!isNaN(newOffset) && newOffset !== currentOffset) {
            currentOffset = newOffset;
            loadExplorerData();
          }
        });

        // Register Inline Cell Editor Click
        this.registerInlineEditor(loadExplorerData);

      } catch (err) {
        console.error("Error loading explorer data:", err);
        this.showToast('error', 'Failed to fetch bond list: ' + err.message);
      }
    };

    // Auto-apply filter dropdowns on change
    $('.explorer-auto-filter').off('change').on('change', () => {
      currentOffset = 0;
      loadExplorerData();
    });

    // Auto-apply debounced search input (300ms)
    let explorerSearchDebounce;
    $('#search-input-box').off('keyup input').on('keyup input', () => {
      clearTimeout(explorerSearchDebounce);
      explorerSearchDebounce = setTimeout(() => {
        currentOffset = 0;
        loadExplorerData();
      }, 300);
    });

    // Delete Bond Record Action (Admin only)
    $(document).off('click', '.btn-delete-bond').on('click', '.btn-delete-bond', async function() {
      const id = $(this).attr('data-bond-id');
      
      const confirm = await Swal.fire({
        title: 'Delete Bond Record?',
        text: 'This will permanently delete this bond reference record and its edit history from the database. This action is irreversible.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a'
      });

      if (confirm.isConfirmed) {
        try {
          await self.apiCall(`/data/bonds/${id}`, {
            method: 'DELETE'
          });
          self.showToast('success', 'Bond reference record permanently deleted.');
          // Clear from expanded tracked states
          self.expandedBondIds.delete(id);
          loadExplorerData();
        } catch (err) {
          self.showToast('error', err.message || 'Failed to delete record.');
        }
      }
    });

    // Excel Export Row
    $(document).on('click', '.btn-export-row', async function() {
      const id = $(this).attr('data-bond-id');
      const token = localStorage.getItem('edi_sig_token');
      try {
        self.showToast('info', 'Preparing Excel export...');
        const res = await fetch(`${API_BASE}/export/excel/${id}?_t=${Date.now()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Export failed.');
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `bond_${id}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        self.showToast('error', err.message || 'Export failed.');
      }
    });

    // Excel Export Bulk Validated
    $('#btn-bulk-export').on('click', async () => {
      const token = localStorage.getItem('edi_sig_token');
      try {
        self.showToast('info', 'Preparing bulk Excel export...');
        const res = await fetch(`${API_BASE}/export/excel/bulk?_t=${Date.now()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Bulk export failed.');
        const blob = await res.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'validated_bonds_bulk.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(blobUrl);
      } catch (err) {
        self.showToast('error', err.message || 'Export failed.');
      }
    });

    // FTP Dispatch Action (Single / Bulk)
    $('#btn-bulk-ftp').on('click', async () => {
      // Fetch FTP destinations
      try {
        const dests = await self.apiCall('/ftp/destinations');
        if (dests.length === 0) {
          self.showToast('error', 'Please configure an FTP destination first in Settings.');
          return;
        }

        const inputOptions = {};
        dests.forEach(d => {
          inputOptions[d.id] = `${d.name} (${d.protocol}://${d.host})`;
        });

        const { value: destId } = await Swal.fire({
          title: 'Select FTP/SFTP Destination',
          input: 'select',
          inputOptions,
          inputPlaceholder: 'Select a server',
          showCancelButton: true,
          confirmButtonColor: '#e52e3d',
          cancelButtonColor: '#71717a'
        });

        if (destId) {
          self.showToast('info', 'FTP dispatch triggered in background.');
          const res = await self.apiCall('/ftp/transfer', {
            method: 'POST',
            body: JSON.stringify({ destinationId: parseInt(destId) })
          });
          self.showToast('success', res.message);
        }

      } catch (err) {
        self.showToast('error', 'FTP Dispatch call failed.');
      }
    });

    // History View Toggle
    $(document).on('click', '.btn-view-history', async function() {
      const id = $(this).attr('data-bond-id');
      const target = $(`#history-${id}`);
      
      if (!target.hasClass('d-none')) {
        target.addClass('d-none');
        return;
      }

      try {
        const logs = await self.apiCall(`/data/bonds/${id}/history`);
        target.empty();
        if (logs.length === 0) {
          target.append('<div>No edit operations logged.</div>');
        } else {
          logs.forEach(l => {
            target.append(`<div>[${new Date(l.created_at).toLocaleString()}] Field '${sanitize(l.field_name)}': '${sanitize(l.old_value || '')}' → '${sanitize(l.new_value)}' (IP: ${sanitize(l.edited_by_ip)})</div>`);
          });
        }
        target.removeClass('d-none');
      } catch (err) {
        self.showToast('error', 'Failed to retrieve edit logs.');
      }
    });

    // Load initial data
    await loadExplorerData();
  },

  // Inline Cell Editor logic (double-click to edit)
  registerInlineEditor(reloadCallback) {
    const self = this;
    $('.editable-cell').off('dblclick').on('dblclick', function() {
      const cell = $(this);
      if (cell.hasClass('cell-editing')) return;

      const bondId = cell.attr('data-bond-id');
      const field = cell.attr('data-field');
      const text = cell.text().trim();

      cell.addClass('cell-editing').html(`<input type="text" value="${text}">`);
      const input = cell.find('input');
      input.focus();

      const finishEdit = async (save) => {
        const val = input.val().trim();
        cell.removeClass('cell-editing').empty().text(val);
        
        if (save && val !== text) {
          // Double check prompt before committing
          const confirm = await Swal.fire({
            title: 'Verify Data Change',
            text: `Update '${field}' value from '${text}' to '${val}'?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e52e3d',
            cancelButtonColor: '#71717a'
          });

          if (confirm.isConfirmed) {
            try {
              await self.apiCall(`/data/bonds/${bondId}`, {
                method: 'PUT',
                body: JSON.stringify({ field, value: val })
              });
              cell.addClass('cell-changed');
              self.showToast('success', 'Field saved successfully.');
              reloadCallback();
            } catch (err) {
              self.showToast('error', err.message || 'Failed to update field.');
              cell.text(text);
            }
          } else {
            cell.text(text);
          }
        } else {
          cell.text(text);
        }
      };

      input.on('keydown', async (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          finishEdit(true);
        } else if (e.key === 'Escape') {
          e.preventDefault();
          finishEdit(false);
        } else if (e.key === 'Tab') {
          e.preventDefault();
          
          const val = input.val().trim();
          input.off('blur'); // unbind blur to prevent double execution
          cell.removeClass('cell-editing').empty().text(val);
          
          if (val !== text) {
            try {
              await self.apiCall(`/data/bonds/${bondId}`, {
                method: 'PUT',
                body: JSON.stringify({ field, value: val })
              });
              cell.addClass('cell-changed');
              self.showToast('success', 'Field saved successfully.');
              reloadCallback();
            } catch (err) {
              self.showToast('error', err.message || 'Failed to update field.');
              cell.text(text);
            }
          } else {
            cell.text(text);
          }
          
          // Locate the next editable cell inside the current row
          const tr = cell.closest('tr');
          const editables = tr.find('.editable-cell');
          const currIdx = editables.index(cell);
          if (currIdx < editables.length - 1) {
            const nextCell = $(editables[currIdx + 1]);
            setTimeout(() => {
              nextCell.trigger('dblclick');
            }, 50);
          }
        }
      });

      input.on('blur', () => {
        setTimeout(() => {
          if (cell.hasClass('cell-editing')) {
            finishEdit(false);
          }
        }, 150);
      });
    });
  },

  // ---------------------------------------------------------------------------
  // Page: Audit Log View (ISO 27001 logs)
  // ---------------------------------------------------------------------------
  async renderAuditLog(container) {
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0">ISO 27001 Access & Operations Logs</h4>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Syslog SIEM Wazuh Exportable Logs</h6>
              <div class="table-responsive">
                <table class="table table-hover" id="audit-log-table" style="vertical-align: middle;">
                  <thead>
                    <tr>
                      <th>Time</th>
                      <th>Event Type</th>
                      <th>Source Module</th>
                      <th>User IP Address</th>
                      <th>User Agent</th>
                    </tr>
                  </thead>
                  <tbody id="audit-log-body">
                    <tr><td colspan="5" class="text-center text-muted">Querying log indices...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);

    try {
      const audits = await this.apiCall('/audit');
      const tbody = $('#audit-log-body').empty();
      
      if (audits.length === 0) {
        tbody.html('<tr><td colspan="5" class="text-center text-muted">No audit events found.</td></tr>');
        return;
      }

      audits.forEach(a => {
        tbody.append(`
          <tr>
            <td class="text-white">${new Date(a.created_at).toLocaleString()}</td>
            <td><span class="badge badge-event">${a.event_type}</span></td>
            <td class="text-white">${a.event_source}</td>
            <td class="font-monospace text-muted">${a.ip_address}</td>
            <td class="tx-12 text-muted" style="max-width: 300px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${a.user_agent}</td>
          </tr>
        `);
      });
    } catch (err) {
      $('#audit-log-body').html('<tr><td colspan="5" class="text-center text-danger">Failed to load log metrics.</td></tr>');
    }
  },

  cronToHumanDescription(cronStr) {
    if (!cronStr) return 'Not scheduled';
    const parts = cronStr.trim().split(/\s+/);
    if (parts.length !== 5) return cronStr;
    const [min, hour, dom, mon, dow] = parts;

    const pad = (n) => (String(n).length === 1 ? '0' + n : n);
    const timeStr = `${pad(hour)}:${pad(min)}`;

    if (dow === '1-5' || dow === 'MON-FRI' || dow === '1,2,3,4,5') {
      return `Weekdays (Mon–Fri) at ${timeStr} CET`;
    }
    if (dow === '*' && dom === '*' && mon === '*') {
      return `Daily at ${timeStr} CET`;
    }
    if (dom === '*' && mon === '*' && dow !== '*') {
      const daysMap = { '0': 'Sun', '1': 'Mon', '2': 'Tue', '3': 'Wed', '4': 'Thu', '5': 'Fri', '6': 'Sat', '7': 'Sun' };
      const dayName = daysMap[dow] || dow;
      return `Weekly on ${dayName} at ${timeStr} CET`;
    }
    return `Custom (${cronStr})`;
  },

  // ---------------------------------------------------------------------------
  // Page: Schedules & Automation Control Console
  // ---------------------------------------------------------------------------
  async renderSchedules(container) {
    const self = this;
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0">Automated Job & Task Scheduling</h4>
        </div>
        <div>
          <button class="btn btn-primary btn-icon-text" id="btn-create-schedule">
            <i class="btn-icon-prepend" data-feather="plus-circle"></i> Create New Schedule
          </button>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Configured Automated Background Tasks</h6>
              <div class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Schedule Name</th>
                      <th>Task Type</th>
                      <th>Frequency & Execution Time</th>
                      <th>Target Module / Destination</th>
                      <th>Status</th>
                      <th>Last Execution</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody id="schedules-table-body">
                    <tr><td colspan="8" class="text-center text-muted">Querying database...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);

    const loadSchedulesTable = async () => {
      try {
        const schedules = await this.apiCall('/schedules');
        const tbody = $('#schedules-table-body').empty();

        if (schedules.length === 0) {
          tbody.html('<tr><td colspan="8" class="text-center text-muted">No scheduled tasks configured.</td></tr>');
          return;
        }

        schedules.forEach(s => {
          const typeBadge = s.task_type === 'scrape' 
            ? '<span class="badge bg-info">ISIN Ingestion Scrapes</span>' 
            : '<span class="badge bg-success">SFTP/FTP Data Feed</span>';
          
          const statusBadge = s.is_active 
            ? '<span class="badge bg-success">Active</span>' 
            : '<span class="badge bg-secondary">Paused</span>';

          const targetInfo = s.task_type === 'scrape'
            ? `<span class="font-monospace text-white">${sanitize(s.exchange_module)}</span>`
            : `<span class="text-white">${sanitize(s.destination_name || 'Default SFTP')}</span>`;

          const lastRun = s.last_run_at ? new Date(s.last_run_at).toLocaleString() : 'Never';
          const humanCron = self.cronToHumanDescription(s.cron_expression);

          tbody.append(`
            <tr>
              <td class="font-monospace">#${s.id}</td>
              <td class="fw-bold text-white">${sanitize(s.name)}</td>
              <td>${typeBadge}</td>
              <td>
                <div class="text-white fw-bold tx-13">${sanitize(humanCron)}</div>
                <code class="text-warning tx-11">${sanitize(s.cron_expression)}</code>
              </td>
              <td>${targetInfo}</td>
              <td>${statusBadge}</td>
              <td class="text-muted tx-13">${lastRun}</td>
              <td>
                <button class="btn btn-xs btn-outline-warning toggle-schedule-btn me-1" data-id="${s.id}" title="Pause/Resume">
                  <i data-feather="${s.is_active ? 'pause' : 'play'}" class="wd-14 ht-14"></i>
                </button>
                <button class="btn btn-xs btn-outline-danger delete-schedule-btn" data-id="${s.id}" title="Delete">
                  <i data-feather="trash-2" class="wd-14 ht-14"></i>
                </button>
              </td>
            </tr>
          `);
        });

        if (typeof feather !== 'undefined') {
          feather.replace();
        }

        // Toggle active button click
        $('.toggle-schedule-btn').on('click', async function() {
          const schedId = $(this).data('id');
          try {
            await self.apiCall(`/schedules/${schedId}/toggle`, { method: 'POST' });
            self.showToast('success', 'Schedule status updated.');
            loadSchedulesTable();
          } catch (err) {
            self.showToast('error', err.message || 'Failed to toggle schedule.');
          }
        });

        // Delete button click
        $('.delete-schedule-btn').on('click', async function() {
          const schedId = $(this).data('id');
          const confirm = await Swal.fire({
            title: 'Delete Scheduled Task?',
            text: 'This will permanently remove this automated schedule.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e52e3d',
            cancelButtonColor: '#71717a',
            confirmButtonText: 'Delete'
          });
          if (confirm.isConfirmed) {
            try {
              await self.apiCall(`/schedules/${schedId}`, { method: 'DELETE' });
              self.showToast('success', 'Schedule deleted.');
              loadSchedulesTable();
            } catch (err) {
              self.showToast('error', err.message || 'Failed to delete schedule.');
            }
          }
        });

      } catch (err) {
        $('#schedules-table-body').html('<tr><td colspan="8" class="text-danger text-center">Failed to load schedules list.</td></tr>');
      }
    };

    await loadSchedulesTable();

    // Create schedule modal trigger
    $('#btn-create-schedule').on('click', async () => {
      let modules = [];
      let ftpDests = [];
      try {
        const rawModules = await self.apiCall('/modules').catch(() => []);
        modules = Array.isArray(rawModules) ? rawModules : (rawModules && Array.isArray(rawModules.modules) ? rawModules.modules : []);
        ftpDests = await self.apiCall('/ftp/destinations').catch(() => []);
      } catch (e) {}

      const moduleOptions = modules.map(m => `<option value="${m.name}">${m.description}</option>`).join('');
      const ftpOptions = ftpDests.map(d => `<option value="${d.id}">${d.name} (${d.host})</option>`).join('');

      const { value: formValues } = await Swal.fire({
        title: 'Create Automated Schedule',
        html: `
          <div class="text-start">
            <div class="mb-3">
              <label class="form-label text-muted">Schedule Name</label>
              <input id="swal-name" class="form-control bg-dark border-secondary text-white" placeholder="e.g. Daily SIX Swiss Ingestion">
            </div>

            <div class="mb-3">
              <label class="form-label text-muted">Task Type</label>
              <select id="swal-type" class="form-select bg-dark border-secondary text-white">
                <option value="scrape">Automated ISIN Ingestion Scrape</option>
                <option value="ftp_export">Automated SFTP/FTP Feed Push</option>
              </select>
            </div>

            <!-- Friendly Frequency & Time Picker -->
            <div class="row g-2 mb-3">
              <div class="col-md-6">
                <label class="form-label text-muted">Frequency</label>
                <select id="swal-freq" class="form-select bg-dark border-secondary text-white">
                  <option value="weekdays" selected>Daily (Mon–Fri Weekdays)</option>
                  <option value="daily">Daily (Every Day)</option>
                  <option value="weekly">Weekly (Specific Day)</option>
                  <option value="custom">Advanced Custom Cron</option>
                </select>
              </div>
              <div class="col-md-6" id="swal-time-col">
                <label class="form-label text-muted">Execution Time (CET)</label>
                <input id="swal-time" type="time" class="form-control bg-dark border-secondary text-white" value="18:00">
              </div>
            </div>

            <div class="mb-3 d-none" id="swal-weekly-day-group">
              <label class="form-label text-muted">Day of Week</label>
              <select id="swal-weekly-day" class="form-select bg-dark border-secondary text-white">
                <option value="1" selected>Monday</option>
                <option value="2">Tuesday</option>
                <option value="3">Wednesday</option>
                <option value="4">Thursday</option>
                <option value="5">Friday</option>
                <option value="6">Saturday</option>
                <option value="0">Sunday</option>
              </select>
            </div>

            <div class="mb-3 d-none" id="swal-custom-cron-group">
              <label class="form-label text-muted">Raw Cron Expression</label>
              <input id="swal-cron" class="form-control bg-dark border-secondary text-white font-monospace" value="0 18 * * 1-5">
              <div class="form-text tx-11 text-muted">Format: min hour day month day-of-week</div>
            </div>

            <!-- Live Summary Preview Banner -->
            <div class="alert bg-dark border border-primary text-info py-2 px-3 mb-3 tx-13" id="swal-cron-preview">
              <i data-feather="clock" class="wd-14 ht-14 me-1"></i> <span id="swal-preview-text">Runs Weekdays (Mon–Fri) at 18:00 CET</span>
            </div>

            <div class="mb-3" id="swal-module-group">
              <label class="form-label text-muted">Exchange Module</label>
              <select id="swal-module" class="form-select bg-dark border-secondary text-white">
                ${moduleOptions || '<option value="boerse_stuttgart">Börse Stuttgart</option><option value="six_swiss">SIX Swiss Exchange</option>'}
              </select>
            </div>

            <div class="mb-3" id="swal-isin-group">
              <div class="d-flex justify-content-between align-items-center mb-1">
                <label class="form-label text-muted mb-0">Target ISIN List</label>
                <span class="badge bg-secondary tx-11" id="swal-isin-count-badge">0 ISINs loaded</span>
              </div>
              <div class="mb-2">
                <input type="file" id="swal-isin-file" accept=".txt,.csv" class="form-control form-control-sm bg-dark border-secondary text-white">
                <div class="form-text tx-11 text-muted">Upload a <code>.txt</code> file (e.g. <em>StuttgartISIN.txt</em>) or type codes manually below.</div>
              </div>
              <textarea id="swal-isins" class="form-control bg-dark border-secondary text-white font-monospace" rows="3" placeholder="CH0441026040, DE0001102309"></textarea>
            </div>

            <div class="mb-3 d-none" id="swal-ftp-group">
              <label class="form-label text-muted">Target SFTP Destination</label>
              <select id="swal-ftp" class="form-select bg-dark border-secondary text-white">
                ${ftpOptions || '<option value="">Default SFTP Target</option>'}
              </select>
            </div>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Save Schedule',
        didOpen: () => {
          // File upload listener for ISIN list file
          $('#swal-isin-file').on('change', function(e) {
            const file = e.target.files[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = function(evt) {
              const text = evt.target.result || '';
              const matches = text.match(/[A-Z0-9]{12}/gi) || [];
              const uniqueIsins = Array.from(new Set(matches.map(i => i.toUpperCase())));
              $('#swal-isins').val(uniqueIsins.join(', '));
              $('#swal-isin-count-badge').text(`${uniqueIsins.length} ISINs loaded`).removeClass('bg-secondary').addClass('bg-info');
            };
            reader.readAsText(file);
          });

          $('#swal-isins').on('input', function() {
            const raw = $(this).val() || '';
            const matches = raw.match(/[A-Z0-9]{12}/gi) || [];
            $('#swal-isin-count-badge').text(`${matches.length} ISINs loaded`);
          });

          const updateCronPreview = () => {
            const freq = $('#swal-freq').val();
            const timeVal = $('#swal-time').val() || '18:00';
            const [hStr, mStr] = timeVal.split(':');
            const hour = parseInt(hStr, 10) || 18;
            const min = parseInt(mStr, 10) || 0;

            let cronExpr = '';
            let summaryText = '';

            if (freq === 'weekdays') {
              cronExpr = `${min} ${hour} * * 1-5`;
              summaryText = `Runs Weekdays (Mon–Fri) at ${timeVal} CET`;
              $('#swal-weekly-day-group, #swal-custom-cron-group').addClass('d-none');
              $('#swal-time-col').removeClass('d-none');
            } else if (freq === 'daily') {
              cronExpr = `${min} ${hour} * * *`;
              summaryText = `Runs Daily at ${timeVal} CET`;
              $('#swal-weekly-day-group, #swal-custom-cron-group').addClass('d-none');
              $('#swal-time-col').removeClass('d-none');
            } else if (freq === 'weekly') {
              const dayVal = $('#swal-weekly-day').val();
              const dayName = $('#swal-weekly-day option:selected').text();
              cronExpr = `${min} ${hour} * * ${dayVal}`;
              summaryText = `Runs Weekly every ${dayName} at ${timeVal} CET`;
              $('#swal-weekly-day-group').removeClass('d-none');
              $('#swal-custom-cron-group').addClass('d-none');
              $('#swal-time-col').removeClass('d-none');
            } else if (freq === 'custom') {
              cronExpr = $('#swal-cron').val() || `${min} ${hour} * * 1-5`;
              summaryText = `Custom Schedule: ${cronExpr}`;
              $('#swal-custom-cron-group').removeClass('d-none');
              $('#swal-weekly-day-group, #swal-time-col').addClass('d-none');
            }

            $('#swal-cron').val(cronExpr);
            $('#swal-preview-text').text(summaryText);
          };

          $('#swal-freq, #swal-time, #swal-weekly-day, #swal-cron').on('change input', updateCronPreview);

          $('#swal-type').on('change', function() {
            if ($(this).val() === 'ftp_export') {
              $('#swal-module-group, #swal-isin-group').addClass('d-none');
              $('#swal-ftp-group').removeClass('d-none');
            } else {
              $('#swal-module-group, #swal-isin-group').removeClass('d-none');
              $('#swal-ftp-group').addClass('d-none');
            }
          });

          updateCronPreview();
        },
        preConfirm: () => {
          return {
            name: $('#swal-name').val(),
            task_type: $('#swal-type').val(),
            cron_expression: $('#swal-cron').val(),
            exchange_module: $('#swal-module').val(),
            isin_list: $('#swal-isins').val(),
            ftp_destination_id: $('#swal-ftp').val() || null,
            is_active: true
          };
        }
      });

      if (formValues && formValues.name) {
        try {
          await self.apiCall('/schedules', {
            method: 'POST',
            body: JSON.stringify(formValues)
          });
          self.showToast('success', 'Automated schedule created successfully.');
          loadSchedulesTable();
        } catch (err) {
          self.showToast('error', err.message || 'Failed to create schedule.');
        }
      }
    });
  },

  // ---------------------------------------------------------------------------
  // Page: Settings View (FTP destinations and configurations)
  // ---------------------------------------------------------------------------
  async renderSettings(container) {
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0">System Control Panel & Integration Options</h4>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Downstream FTP/SFTP Integration Servers</h6>
              
              <!-- Form for New Destination -->
              <form id="form-add-ftp" class="mb-4">
                <div class="row g-3">
                  <div class="col-12">
                    <label class="form-label text-muted">Destination Name</label>
                    <input type="text" class="form-control bg-dark border-secondary text-white" id="ftp-name" placeholder="e.g. Frankfurt Mainframe" required>
                  </div>
                  <div class="col-md-8">
                    <label class="form-label text-muted">Host Server Address</label>
                    <input type="text" class="form-control bg-dark border-secondary text-white" id="ftp-host" placeholder="ftp.mycompany.de" required>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label text-muted">Port</label>
                    <input type="number" class="form-control bg-dark border-secondary text-white" id="ftp-port" placeholder="22" value="22" required>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label text-muted">Protocol Type</label>
                    <select class="form-select bg-dark border-secondary text-white" id="ftp-protocol">
                      <option value="sftp" selected>SFTP (SSH)</option>
                      <option value="ftp">FTP (Plaintext)</option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label text-muted">Username</label>
                    <input type="text" class="form-control bg-dark border-secondary text-white" id="ftp-user" placeholder="login" required>
                  </div>
                  <div class="col-12">
                    <label class="form-label text-muted">Security Password</label>
                    <input type="password" class="form-control bg-dark border-secondary text-white" id="ftp-pass" placeholder="Password" required>
                  </div>
                  <div class="col-12">
                    <label class="form-label text-muted">Remote Ingestion Path</label>
                    <input type="text" class="form-control bg-dark border-secondary text-white" id="ftp-path" placeholder="/ingest/bonds/" value="/" required>
                  </div>
                  <div class="col-12 mt-4">
                    <button type="submit" class="btn btn-danger w-100">Add Remote Connection</button>
                  </div>
                </div>
              </form>

            </div>
          </div>
        </div>

        <div class="col-md-6 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Configured FTP Servers Allowlist</h6>
              <div class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Server</th>
                      <th>Protocol</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody id="ftp-dest-body">
                    <tr><td colspan="3" class="text-center text-muted">Loading list...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-4">
        <div class="col-md-12 stretch-card">
          <div class="card card-glass border-info">
            <div class="card-body">
              <h6 class="card-title text-info d-flex align-items-center gap-2">
                <i data-feather="cpu" class="wd-18 ht-18"></i>
                Modular Core: Discovered Exchange Scraper Plugins
              </h6>
              <p class="text-muted tx-13 mb-3">
                Active exchange connectors dynamically registered with this gateway. Choose a module below to launch a data ingestion job, or inspect plugin health status.
              </p>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Module Key</th>
                      <th>Plugin Description / Target Exchange</th>
                      <th>Status</th>
                      <th>Quick Action</th>
                    </tr>
                  </thead>
                  <tbody id="discovered-modules-body">
                    <tr><td colspan="4" class="text-center text-muted">Querying backend...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);

    const self = this;
    
    // Load Discovered modules dynamically
    const reloadModules = async () => {
      try {
        const rawModules = await self.apiCall('/modules');
        const modules = Array.isArray(rawModules) ? rawModules : (rawModules && Array.isArray(rawModules.modules) ? rawModules.modules : []);
        const tbody = $('#discovered-modules-body').empty();
        if (modules.length === 0) {
          tbody.html('<tr><td colspan="4" class="text-center text-muted">No scraper plugins discovered.</td></tr>');
          return;
        }
        modules.forEach(m => {
          const isProductionActive = m.name === 'boerse_stuttgart';
          const statusBadge = isProductionActive
            ? '<span class="badge bg-success"><i data-feather="check-circle" class="me-1" style="width:12px;height:12px;"></i>Active / Production</span>'
            : '<span class="badge bg-secondary"><i data-feather="box" class="me-1" style="width:12px;height:12px;"></i>Placeholder / Preview</span>';

          tbody.append(`
            <tr>
              <td><span class="badge bg-dark border border-secondary text-white font-monospace">${m.name}</span></td>
              <td class="text-white">${m.description}</td>
              <td>${statusBadge}</td>
              <td>
                <a href="#/batch-qa?module=${encodeURIComponent(m.name)}" class="btn btn-xs ${isProductionActive ? 'btn-outline-info' : 'btn-outline-secondary opacity-75'}">
                  <i data-feather="play-circle" class="me-1" style="width:12px;height:12px;"></i>Start Ingestion Job
                </a>
              </td>
            </tr>
          `);
        });
        if (typeof feather !== 'undefined') feather.replace();
      } catch (e) {
        $('#discovered-modules-body').html('<tr><td colspan="4" class="text-danger">Failed to load plugins.</td></tr>');
      }
    };

    const reloadDests = async () => {
      try {
        const dests = await self.apiCall('/ftp/destinations');
        const tbody = $('#ftp-dest-body').empty();
        
        if (dests.length === 0) {
          tbody.html('<tr><td colspan="3" class="text-center text-muted">No FTP connections configured.</td></tr>');
          return;
        }

        dests.forEach(d => {
          tbody.append(`
            <tr>
              <td>
                <div class="text-white fw-bold">${sanitize(d.name)}</div>
                <div class="tx-12 text-muted">${sanitize(d.username)}@${sanitize(d.host)}:${sanitize(d.port)}</div>
              </td>
              <td><span class="badge bg-secondary font-monospace">${sanitize(d.protocol ? d.protocol.toUpperCase() : 'SFTP')}</span></td>
              <td>
                <div class="d-flex gap-2">
                  <button class="btn btn-xs btn-outline-info btn-test-ftp" data-id="${d.id}">Test</button>
                  <button class="btn btn-xs btn-danger btn-delete-ftp" data-id="${d.id}"><i data-feather="trash-2"></i></button>
                </div>
              </td>
            </tr>
          `);
        });

        if (typeof feather !== 'undefined') {
          feather.replace();
        }

      } catch (err) {
        $('#ftp-dest-body').html('<tr><td colspan="3" class="text-danger">Failed to load destinations.</td></tr>');
      }
    };

    // Form Submission for Add Destination
    $('#form-add-ftp').on('submit', async (e) => {
      e.preventDefault();
      
      const payload = {
        name: $('#ftp-name').val(),
        host: $('#ftp-host').val(),
        port: parseInt($('#ftp-port').val()),
        protocol: $('#ftp-protocol').val(),
        username: $('#ftp-user').val(),
        password: $('#ftp-pass').val(),
        remotePath: $('#ftp-path').val()
      };

      try {
        await self.apiCall('/ftp/destinations', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        self.showToast('success', 'FTP connection saved.');
        $('#form-add-ftp')[0].reset();
        await reloadDests();
      } catch (err) {
        self.showToast('error', 'Failed to save connection.');
      }
    });

    // Test Connection Action
    $(document).on('click', '.btn-test-ftp', async function() {
      const id = $(this).attr('data-id');
      self.showToast('info', 'Initiating connection test...');
      
      try {
        const res = await self.apiCall(`/ftp/destinations/${id}/test`, { method: 'POST' });
        if (res.success) {
          self.showToast('success', 'Integration connection confirmed.');
        } else {
          self.showToast('error', 'Connection verification failed.');
        }
      } catch (err) {
        self.showToast('error', err.message || 'Connection test failed.');
      }
    });

    // Delete Connection Action
    $(document).on('click', '.btn-delete-ftp', async function() {
      const id = $(this).attr('data-id');
      const confirm = await Swal.fire({
        title: 'Delete FTP Destination?',
        text: 'This will remove this target configuration from the system database.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a'
      });

      if (confirm.isConfirmed) {
        try {
          await self.apiCall(`/ftp/destinations/${id}`, { method: 'DELETE' });
          self.showToast('success', 'Destination configuration deleted.');
          await reloadDests();
        } catch (err) {
          self.showToast('error', 'Deletion failed.');
        }
      }
    });

    await reloadDests();
    await reloadModules();
  },

  // ---------------------------------------------------------------------------
  // Page: User Management View (Admin Only)
  // ---------------------------------------------------------------------------
  async renderUserManagement(container) {
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0 text-navy">Identity & Access Control — User Directory</h4>
        </div>
      </div>

      <div class="row">
        <!-- User Form -->
        <div class="col-md-4 grid-margin stretch-card">
          <div class="card card-glass border-navy">
            <div class="card-body">
              <h6 class="card-title text-navy d-flex align-items-center gap-2">
                <i data-feather="user-plus" class="wd-18 ht-18"></i>
                Provision New Account
              </h6>
              <form id="form-add-user" class="mt-3">
                <div class="mb-3">
                  <label class="form-label text-muted">Username</label>
                  <input type="text" class="form-control bg-dark border-secondary text-white" id="user-username" placeholder="j.doe" required>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted">Full Name</label>
                  <input type="text" class="form-control bg-dark border-secondary text-white" id="user-fullname" placeholder="John Doe" required>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted">Email Address</label>
                  <input type="email" class="form-control bg-dark border-secondary text-white" id="user-email" placeholder="john.doe@company.com" required>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted">Initial Password</label>
                  <input type="password" class="form-control bg-dark border-secondary text-white" id="user-password" placeholder="••••••••" required>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted">Assigned Security Role</label>
                  <select class="form-select bg-dark border-secondary text-white" id="user-role" required>
                    <option value="" disabled selected>Select security role</option>
                  </select>
                </div>
                <button type="submit" class="btn btn-danger w-100 mt-2">Provision User</button>
              </form>
            </div>
          </div>
        </div>

        <!-- Users Directory Table -->
        <div class="col-md-8 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title d-flex justify-content-between align-items-center">
                <span>Active Users Registry</span>
                <span class="badge bg-secondary font-monospace" id="users-count">0 Users</span>
              </h6>
              <div class="table-responsive mt-3">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>User Details</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last Login / Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody id="users-list-body">
                    <tr><td colspan="5" class="text-center text-muted">Querying users directory...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);

    const self = this;
    
    // Load Roles into select dropdown
    const loadRolesDropdown = async () => {
      try {
        const roles = await self.apiCall('/admin/roles');
        self.cachedRoles = roles; // Cache roles for edit modal
        const select = $('#user-role').empty().append('<option value="" disabled selected>Select security role</option>');
        roles.forEach(r => {
          select.append(`<option value="${r.id}">${r.name.toUpperCase()} (${r.description})</option>`);
        });
      } catch (err) {
        self.showToast('error', 'Failed to load roles list.');
      }
    };

    // Reload Users List Table
    const reloadUsers = async () => {
      try {
        const users = await self.apiCall('/admin/users');
        const tbody = $('#users-list-body').empty();
        $('#users-count').text(`${users.length} Users`);

        if (users.length === 0) {
          tbody.html('<tr><td colspan="5" class="text-center text-muted">No accounts provisioned.</td></tr>');
          return;
        }

        users.forEach(u => {
          const statusBadge = u.is_active 
            ? `<span class="badge bg-success-light text-success">Active</span>`
            : `<span class="badge bg-danger-light text-danger">Disabled</span>`;
          
          const lastLoginText = u.last_login 
            ? new Date(u.last_login).toLocaleString()
            : '<em class="text-muted tx-11">Never logged in</em>';

          const safeFullName = sanitize(u.full_name || u.username);
          const safeUsername = sanitize(u.username);
          const safeEmail = sanitize(u.email || 'No Email');
          const safeRole = sanitize(u.role_name ? u.role_name.toUpperCase() : 'USER');

          tbody.append(`
            <tr>
              <td>
                <div class="text-white fw-bold">${safeFullName}</div>
                <div class="tx-12 text-muted">@${safeUsername} • ${safeEmail}</div>
              </td>
              <td><span class="badge bg-dark border border-secondary text-white font-monospace">${safeRole}</span></td>
              <td>${statusBadge}</td>
              <td>
                <div class="text-white tx-12">Login: ${lastLoginText}</div>
                <div class="text-muted tx-11">Created: ${new Date(u.created_at).toLocaleDateString()}</div>
              </td>
              <td>
                <div class="d-flex gap-2">
                  <button class="btn btn-xs ${u.is_active ? 'btn-outline-warning' : 'btn-outline-success'} btn-toggle-status" data-id="${u.id}" data-active="${u.is_active}" title="${u.is_active ? 'Disable User' : 'Enable User'}">
                    <i data-feather="${u.is_active ? 'user-x' : 'user-check'}" style="width: 14px; height: 14px;"></i>
                  </button>
                  <button class="btn btn-xs btn-outline-info btn-reset-password" data-id="${u.id}" data-username="${safeUsername}" title="Reset Password">
                    <i data-feather="key" style="width: 14px; height: 14px;"></i>
                  </button>
                  <button class="btn btn-xs btn-outline-primary btn-edit-user" data-id="${u.id}" data-username="${safeUsername}" data-name="${sanitize(u.full_name || '')}" data-email="${safeEmail}" data-role="${u.role_id}" title="Edit User Details">
                    <i data-feather="edit-2" style="width: 14px; height: 14px;"></i>
                  </button>
                  ${u.username === 'admin'
                    ? `<button class="btn btn-xs btn-outline-secondary" disabled title="System administrator account cannot be deleted"><i data-feather="trash-2" style="width: 14px; height: 14px;"></i></button>`
                    : `<button class="btn btn-xs btn-outline-danger btn-delete-user" data-id="${u.id}" data-username="${safeUsername}" title="Delete User Account"><i data-feather="trash-2" style="width: 14px; height: 14px;"></i></button>`
                  }
                </div>
              </td>
            </tr>
          `);
        });

        if (typeof feather !== 'undefined') {
          feather.replace();
        }
      } catch (err) {
        $('#users-list-body').html('<tr><td colspan="5" class="text-center text-danger">Failed to load user registry.</td></tr>');
      }
    };

    // Handle Form Submission to Provision User
    $('#form-add-user').off('submit').on('submit', async (e) => {
      e.preventDefault();
      const payload = {
        username: $('#user-username').val().trim(),
        full_name: $('#user-fullname').val().trim(),
        email: $('#user-email').val().trim(),
        password: $('#user-password').val(),
        role_id: parseInt($('#user-role').val())
      };

      try {
        await self.apiCall('/admin/users', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        self.showToast('success', 'New user provisioned successfully.');
        $('#form-add-user')[0].reset();
        await reloadUsers();
      } catch (err) {
        self.showToast('error', err.message || 'Failed to provision user.');
      }
    });

    // Toggle User Active Status Action
    $(document).off('click', '.btn-toggle-status').on('click', '.btn-toggle-status', async function() {
      const id = $(this).attr('data-id');
      const currentActive = $(this).attr('data-active') === 'true';
      const actionText = currentActive ? 'Deactivate' : 'Activate';

      const confirm = await Swal.fire({
        title: `${actionText} User Account?`,
        text: `Are you sure you want to ${actionText.toLowerCase()} this user's privileges?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a'
      });

      if (confirm.isConfirmed) {
        try {
          await self.apiCall(`/admin/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ is_active: !currentActive })
          });
          self.showToast('success', `User successfully ${currentActive ? 'deactivated' : 'activated'}.`);
          await reloadUsers();
        } catch (err) {
          self.showToast('error', err.message || 'Status change failed.');
        }
      }
    });

    // Reset User Password Action
    $(document).off('click', '.btn-reset-password').on('click', '.btn-reset-password', async function() {
      const id = $(this).attr('data-id');
      const username = $(this).attr('data-username');

      const { value: newPassword } = await Swal.fire({
        title: `Reset Password for @${username}`,
        input: 'password',
        inputLabel: 'Specify New Security Password',
        inputPlaceholder: 'Enter new password',
        inputAttributes: {
          autocapitalize: 'off',
          autocorrect: 'off'
        },
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a'
      });

      if (newPassword) {
        try {
          await self.apiCall(`/admin/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify({ password: newPassword })
          });
          self.showToast('success', 'User password updated.');
        } catch (err) {
          self.showToast('error', err.message || 'Password reset failed.');
        }
      }
    });

    // Edit User Info Details Action
    $(document).off('click', '.btn-edit-user').on('click', '.btn-edit-user', async function() {
      const id = $(this).attr('data-id');
      const username = $(this).attr('data-username');
      const name = $(this).attr('data-name');
      const email = $(this).attr('data-email');
      const currentRoleId = parseInt($(this).attr('data-role'));

      let roleOptionsHtml = '';
      if (self.cachedRoles) {
        self.cachedRoles.forEach(r => {
          const selected = r.id === currentRoleId ? 'selected' : '';
          roleOptionsHtml += `<option value="${r.id}" ${selected}>${r.name.toUpperCase()} (${r.description})</option>`;
        });
      }

      const { value: formValues } = await Swal.fire({
        title: `Edit User Info: @${username}`,
        html: `
          <div class="mb-3 text-start">
            <label class="form-label text-muted">Full Name</label>
            <input id="edit-fullname" class="swal2-input m-0 w-100 bg-dark text-white border-secondary" placeholder="Full Name" value="${name}">
          </div>
          <div class="mb-3 text-start">
            <label class="form-label text-muted">Email Address</label>
            <input id="edit-email" class="swal2-input m-0 w-100 bg-dark text-white border-secondary" placeholder="Email Address" value="${email}">
          </div>
          <div class="mb-3 text-start">
            <label class="form-label text-muted">Security Role Assignment</label>
            <select id="edit-role" class="swal2-input m-0 w-100 bg-dark text-white border-secondary select-role-edit">
              ${roleOptionsHtml}
            </select>
          </div>
        `,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a',
        preConfirm: () => {
          return {
            full_name: document.getElementById('edit-fullname').value.trim(),
            email: document.getElementById('edit-email').value.trim(),
            role_id: parseInt(document.getElementById('edit-role').value)
          };
        }
      });

      if (formValues) {
        try {
          await self.apiCall(`/admin/users/${id}`, {
            method: 'PUT',
            body: JSON.stringify(formValues)
          });
          self.showToast('success', 'User registry account updated successfully.');
          await reloadUsers();
        } catch (err) {
          self.showToast('error', err.message || 'Failed to update user info.');
        }
      }
    });

    // Delete User Account Action (Hard delete)
    $(document).off('click', '.btn-delete-user').on('click', '.btn-delete-user', async function() {
      const id = $(this).attr('data-id');
      const username = $(this).attr('data-username');

      const confirm = await Swal.fire({
        title: `Permanently Delete @${username}?`,
        text: "This action is irreversible. The account registry will be permanently deleted from the database.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a'
      });

      if (confirm.isConfirmed) {
        try {
          await self.apiCall(`/admin/users/${id}`, {
            method: 'DELETE'
          });
          self.showToast('success', `User account @${username} has been deleted successfully.`);
          await reloadUsers();
        } catch (err) {
          self.showToast('error', err.message || 'Deletion failed.');
        }
      }
    });

    await loadRolesDropdown();
    await reloadUsers();
  },

  // ---------------------------------------------------------------------------
  // Page: Role & Permission Management (Admin Only)
  // ---------------------------------------------------------------------------
  async renderRoleManagement(container) {
    container.html(`
      <div class="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 class="mb-3 mb-md-0 text-navy">Identity & Access Control — Roles & Permissions Matrix</h4>
        </div>
      </div>

      <div class="row">
        <!-- Add Role Form -->
        <div class="col-md-5 grid-margin stretch-card">
          <div class="card card-glass border-navy">
            <div class="card-body">
              <h6 class="card-title text-navy d-flex align-items-center gap-2">
                <i data-feather="shield" class="wd-18 ht-18"></i>
                Define Custom Security Role
              </h6>
              
              <form id="form-add-role" class="mt-3">
                <div class="mb-3">
                  <label class="form-label text-muted">Role Name</label>
                  <input type="text" class="form-control bg-dark border-secondary text-white" id="role-name" placeholder="e.g. analyst" required>
                </div>
                <div class="mb-3">
                  <label class="form-label text-muted">Description / Business Function</label>
                  <input type="text" class="form-control bg-dark border-secondary text-white" id="role-desc" placeholder="Ingests and edits bonds metadata" required>
                </div>
                
                <div class="mb-3">
                  <label class="form-label text-muted fw-bold text-navy">Resource Permission Matrix</label>
                  <div class="table-responsive">
                    <table class="table table-bordered table-sm text-center align-middle mt-1" style="border-color: #3f3f46;">
                      <thead>
                        <tr class="tx-11 text-muted">
                          <th>Resource</th>
                          <th>Read</th>
                          <th>Create</th>
                          <th>Edit</th>
                          <th>Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${['bonds', 'jobs', 'ftp', 'audit', 'users', 'settings'].map(res => `
                          <tr>
                            <td class="text-start text-white fw-bold tx-12 font-monospace">${res}</td>
                            <td><input type="checkbox" class="perm-cb form-check-input" data-res="${res}" data-act="read"></td>
                            <td><input type="checkbox" class="perm-cb form-check-input" data-res="${res}" data-act="create"></td>
                            <td><input type="checkbox" class="perm-cb form-check-input" data-res="${res}" data-act="edit"></td>
                            <td><input type="checkbox" class="perm-cb form-check-input" data-res="${res}" data-act="delete"></td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                <button type="submit" class="btn btn-danger w-100 mt-2">Create Custom Role</button>
              </form>
            </div>
          </div>
        </div>

        <!-- Role List Table -->
        <div class="col-md-7 grid-margin stretch-card">
          <div class="card card-glass">
            <div class="card-body">
              <h6 class="card-title">Configured Security Roles Matrix</h6>
              <div class="table-responsive mt-3">
                <table class="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Role & Scope</th>
                      <th>Granular Scope Mapping</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody id="roles-matrix-body">
                    <tr><td colspan="3" class="text-center text-muted">Querying roles...</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    `);

    const self = this;

    // Reload Roles Matrix List Table
    const reloadRoles = async () => {
      try {
        const roles = await self.apiCall('/admin/roles');
        const tbody = $('#roles-matrix-body').empty();

        roles.forEach(r => {
          const sysBadge = r.is_system 
            ? `<span class="badge bg-secondary font-monospace tx-9 ms-1" style="vertical-align: middle;">SYSTEM</span>`
            : '';

          // Format permissions into grouped badges
          let permBadgesHtml = '';
          if (r.permissions && r.permissions.length > 0) {
            // Group permissions by resource
            const grouped = {};
            r.permissions.forEach(p => {
              const [res, act] = p.split(':');
              if (!grouped[res]) grouped[res] = [];
              grouped[res].push(act);
            });

            permBadgesHtml = Object.keys(grouped).map(res => {
              const acts = grouped[res].join(',');
              return `<div class="mb-1"><span class="badge bg-dark border border-secondary text-white font-monospace tx-11">${res}</span> <span class="tx-11 text-muted font-monospace">${acts}</span></div>`;
            }).join('');
          } else {
            permBadgesHtml = '<em class="text-muted tx-11">No permissions assigned</em>';
          }

          const safeRoleName = sanitize(r.name ? r.name.toUpperCase() : '');
          const safeRoleDesc = sanitize(r.description || 'No description');

          const deleteBtn = r.is_system
            ? `<button class="btn btn-xs btn-outline-secondary" disabled title="System roles cannot be deleted"><i data-feather="trash-2" style="width: 14px; height: 14px;"></i></button>`
            : `<button class="btn btn-xs btn-danger btn-delete-role" data-id="${r.id}" data-name="${sanitize(r.name)}"><i data-feather="trash-2" style="width: 14px; height: 14px;"></i></button>`;

          tbody.append(`
            <tr>
              <td>
                <div class="text-white fw-bold font-monospace">${safeRoleName}${sysBadge}</div>
                <div class="tx-12 text-muted">${safeRoleDesc}</div>
              </td>
              <td>${permBadgesHtml}</td>
              <td>
                <div class="d-flex gap-2">
                  ${deleteBtn}
                </div>
              </td>
            </tr>
          `);
        });

        if (typeof feather !== 'undefined') {
          feather.replace();
        }
      } catch (err) {
        $('#roles-matrix-body').html('<tr><td colspan="3" class="text-center text-danger">Failed to load roles matrix.</td></tr>');
      }
    };

    // Handle Custom Role Creation Form
    $('#form-add-role').off('submit').on('submit', async (e) => {
      e.preventDefault();
      
      const permissions = [];
      $('.perm-cb:checked').each(function() {
        const res = $(this).attr('data-res');
        const act = $(this).attr('data-act');
        permissions.push(`${res}:${act}`);
      });

      const payload = {
        name: $('#role-name').val().trim().toLowerCase(),
        description: $('#role-desc').val().trim(),
        permissions
      };

      if (permissions.length === 0) {
        self.showToast('error', 'Select at least one resource permission checkbox.');
        return;
      }

      try {
        await self.apiCall('/admin/roles', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        self.showToast('success', 'Custom security role registered.');
        $('#form-add-role')[0].reset();
        await reloadRoles();
      } catch (err) {
        self.showToast('error', err.message || 'Failed to create role.');
      }
    });

    // Delete Custom Role Action
    $(document).off('click', '.btn-delete-role').on('click', '.btn-delete-role', async function() {
      const id = $(this).attr('data-id');
      const name = $(this).attr('data-name');

      const confirm = await Swal.fire({
        title: `Delete Role '${name.toUpperCase()}'?`,
        text: 'This action is irreversible and requires no active users to be assigned to this role.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e52e3d',
        cancelButtonColor: '#71717a'
      });

      if (confirm.isConfirmed) {
        try {
          await self.apiCall(`/admin/roles/${id}`, { method: 'DELETE' });
          self.showToast('success', 'Security role deleted.');
          await reloadRoles();
        } catch (err) {
          self.showToast('error', err.message || 'Deletion failed. Ensure no users are assigned.');
        }
      }
    });

    await reloadRoles();
  }
};

window.EDISIG = EDISIG;

$(document).ready(() => {
  EDISIG.init();
});
